import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Environment, ContactShadows } from '@react-three/drei';
import * as THREE from 'three';
import Joystick from './elements/joystick.jsx';
// المان‌های سه‌بعدیِ قابل‌تنظیمِ پلان (دیوار/در/پنجره/کف/سقف) — هرکدام در فایل جداگانه‌ی خودشان زیر
// پوشه‌ی element3D، تا بعداً بشود مرحله‌به‌مرحله (رنگ، متریال، حالت‌های مختلف، المان‌های اضافه‌ی
// جدید مثل سرویس بهداشتی فرنگی و غیره) روی هرکدام جدا کار کرد بدون این‌که به بقیه دست بخورد
import WallSegment, { resolveWallSides, DEFAULT_WALL_SIDE_STYLE, ThresholdBlock, OpeningFrame, openingProtrusionCm } from './element3D/Wall3D.jsx';
import WallStyleBox from './element3D/wallStyleBox.jsx';
import Door, { resolveDoorStyle, DEFAULT_DOOR_STYLE, DOOR_CASING_OVERLAP_CM } from './element3D/Door3D.jsx';
import DoorStyleBox from './element3D/doorStyleBox.jsx';
import OpeningStyleBox from './element3D/openingStyleBox.jsx';
import Window, { resolveWindowStyle, DEFAULT_WINDOW_STYLE } from './element3D/Window3D.jsx';
import WindowStyleBox from './element3D/windowStyleBox.jsx';
import Floor, { resolveFloorStyle, DEFAULT_FLOOR_STYLE } from './element3D/Floor3D.jsx';
import FloorStyleBox from './element3D/floorStyleBox.jsx';
import Ceiling, { resolveCeilingStyle, DEFAULT_CEILING_STYLE } from './element3D/Ceiling3D.jsx';
import CeilingStyleBox from './element3D/ceilingStyleBox.jsx';
import { computeRoomLightLevels, RoomLightingController } from './element3D/roomLighting.jsx';
import { spaceContains, spaceCenter, isTrapPiece, computeSpaces } from './elements/spaces.jsx';
import { buildSurfaceGeometry, piecesFrame } from './element3D/spacePieces.jsx';
import CurvedWall3D from './element3D/CurvedWall3D.jsx';
import { entranceSpawn } from './elements/entrance.jsx';
import { LivePeers, LiveReporter } from './element3D/liveAvatar.jsx';
import LiveHud from './elements/liveHud.jsx';
import EngineeringGlasses from './element3D/engineeringGlasses.jsx';
import { SiteSky } from './element3D/SiteScene.jsx';
import { SceneExporter, RenderUploadButton } from './element3D/renderUpload.jsx';
import { BakedModel } from './element3D/bakedModel.jsx';
import { LocalBakeView } from './element3D/localBake.jsx';
import { FrameCapture, compressBlob, compressPhoto } from './element3D/designExport.jsx';
import SaveDesignSheet from './element3D/saveDesignSheet.jsx';
import { recordDesignFilm } from './element3D/filmRecorder.jsx';
import { SsaoPass, SSAO_ON } from './element3D/ssaoPass.jsx';
import {
  IconCube,
  IconWalk,
  IconPlan,
  IconSun,
  IconPause,
  IconPlay,
  IconStop,
  IconClose,
  IconGlasses,
  IconServer,
  IconUpload,
  IconCheck,
} from './element3D/hudIcons.jsx';
import { unlockAudio, playFootstep, playDoorSound, playWindowSound } from './element3D/sounds.jsx';
import './create3D.scss';

// تبدیل واحد نقشه دو‌بعدی (سانتی‌متر) به واحد صحنه سه‌بعدی (متر) — دوربین، نور و OrbitControls
// پیش‌فرض‌های three.js همه بر مبنای مقیاس «متر» تنظیم شده‌اند، برای همین دیوارها/کف‌ها داخل یک
// <group scale={M}> رندر می‌شوند تا بتوانند با همان مقادیر خام سانتی‌متری (wall.x1, ...) کار کنند
const M = 0.01;

const WALL_HEIGHT_CM = 280;
const DOOR_HEIGHT_CM = 210;
// بازشو (ورودیِ بدونِ در): ارتفاعِ دهانه — بالایش نعل‌درگاه (دیوار) و دورش چارچوب
const OPENING_HEIGHT_CM = 240;
const WINDOW_SILL_CM = 90;
const WINDOW_HEADER_CM = 210;
const FLOOR_PALETTE = ['#e7dcc9', '#d9e4dd', '#dce4ee', '#ecdfd8', '#e1e1ea', '#e9e0e1'];

// محدوده‌ی عمودیِ یک پنجره‌ی خاص — اگر کاربر از پنلِ ادیتِ پنجره ارتفاع/ارتفاعِ‌از‌کف را عوض کرده
// باشد (win.style.sillCm/heightCm)، همان‌ها اولویت دارند؛ وگرنه به مقادیرِ سراسریِ پیش‌فرض برمی‌گردیم —
// دقیقاً همان الگویی که برای عرض/ارتفاعِ اختصاصیِ در (entry.style?.widthCm) استفاده شده
function getWindowVerticalRange(winData) {
  const sillCm = winData?.style?.sillCm != null ? winData.style.sillCm : WINDOW_SILL_CM;
  const heightCm = winData?.style?.heightCm || WINDOW_HEADER_CM - WINDOW_SILL_CM;
  return { sillCm, heightCm, headerCm: sillCm + heightCm };
}

// ثابت‌های حالت اول‌شخص (راه رفتن داخل پلان)
const EYE_HEIGHT_CM = 165; // ارتفاع چشم از کف
// سرعتِ دنبال‌کردنِ ارتفاعِ کف موقعِ رد شدن از اختلافِ سطح (۱/ثانیه) — حدودِ ۰٫۱ ثانیه: یک قدمِ کوتاه و مشخص، نه سُر خوردن روی شیب
const GROUND_FOLLOW_RATE = 22;
const WALK_MOVE_SPEED_CM_S = 160; // سرعت راه رفتن معمولی یک بزرگسال
const LOOK_SENSITIVITY = 0.0035; // رادیان چرخش به ازای هر پیکسل درگ روی صفحه
const PLAYER_RADIUS_CM = 28; // شعاع تقریبی بدن کاربر، برای برخورد با دیوار/پنجره
const PITCH_LIMIT = Math.PI / 2.2; // محدودیت نگاه‌کردن به بالا/پایین (کمی کمتر از عمود کامل)

// «راه رفتنِ آدم» به‌جای سُرخوردنِ دوربین:
// شتاب/ایستِ نرم — سرعت یک‌باره صفر یا ماکسیمم نمی‌شود (مثلِ بدنِ واقعی که کمی طول می‌کشد راه بیفتد/بایستد)
const WALK_ACCEL_RATE = 7; // هرچه بزرگ‌تر، سریع‌تر به سرعتِ هدف می‌رسد (۱/ثانیه)
const WALK_DECEL_RATE = 9;
// حرکتِ سر در هر قدم: یک بالا-پایینِ کوچک در هر قدم + یک تابِ ملایمِ چپ-راست در هر دو قدم (یک گام کامل)
const STEP_LENGTH_CM = 75; // طولِ تقریبیِ یک قدمِ بزرگسال (با سرعتِ فعلی ≈ ۲ قدم در ثانیه، آهنگِ عادیِ راه رفتن)
const HEAD_BOB_CM = 2.2; // دامنه‌ی بالا-پایینِ سر
const HEAD_SWAY_CM = 1.5; // دامنه‌ی تابِ چپ-راست
const HEAD_ROLL_RAD = 0.006; // کجیِ خیلی جزئیِ سر همراهِ تاب
const GAIT_BLEND_RATE = 6; // سرعتِ آمدن/رفتنِ حرکتِ سر با شروع/توقفِ راه رفتن
// برگشتِ خودکارِ نگاه به روبه‌رو «حینِ» راه رفتن: اول حرکت شروع می‌شود و نگاه تکان نمی‌خورد (AUTO_LEVEL_DELAY_S)،
// بعد زاویه‌ی بالا/پایین با یک منحنیِ نرم (شروع و پایانِ آرام) صاف می‌شود و دقیقاً AUTO_LEVEL_DURATION_S ثانیه
// بعد از شروعِ راه رفتن به افق می‌رسد. اگر کاربر وسطِ راه بایستد یا خودش نگاه را تکان دهد، همان‌جا متوقف
// می‌شود و در حرکتِ بعدی از زاویه‌ی فعلی دوباره شروع می‌کند
const AUTO_LEVEL_DURATION_S = 1.5;
const AUTO_LEVEL_DELAY_S = 0.25; // اولِ راه رفتن، فقط حرکت (بدونِ تغییرِ زاویه)
const AUTO_LEVEL_IDLE_S = 0.4; // بعد از آخرین تکانِ انگشت، کمی صبر (تا وسطِ یک نگاه‌کردن قاطی نشود)
const AUTO_LEVEL_MIN_SPEED_CM_S = 25; // فقط وقتی واقعاً در حالِ راه رفتن است

// منحنیِ نرمِ ۰→۱ (شیب صفر در دو سر) — «smootherstep»
function smootherstep(x) {
  const t = clamp(x, 0, 1);
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function getWallItemSpan(wall, ratio, itemWidth) {
  const wallLength = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
  const halfWidthRatio = wallLength > 0 ? itemWidth / 2 / wallLength : 0;
  const clampedRatio = Math.max(halfWidthRatio, Math.min(1 - halfWidthRatio, ratio));
  const start = clampedRatio * wallLength - itemWidth / 2;
  const end = start + itemWidth;
  return { start: Math.max(0, start), end: Math.min(wallLength, end) };
}

function buildWallSegments(wallLength, rawSpans) {
  const spans = [...rawSpans].sort((a, b) => a.start - b.start);
  const segments = [];
  let cursor = 0;
  spans.forEach((span) => {
    if (span.start > cursor + 0.01) segments.push({ start: cursor, end: span.start, kind: 'solid' });
    segments.push(span);
    cursor = Math.max(cursor, span.end);
  });
  if (cursor < wallLength - 0.01) segments.push({ start: cursor, end: wallLength, kind: 'solid' });
  return segments;
}

const WALL_OVERLAP_TOLERANCE_CM = 6; // نزدیک به نصف ضخامت دیوار — جهت تحمل خطای اعشاری اسنپ مغناطیسی

// ---------------------------------------------------------------------------------------------------
// اختلافِ سطحِ کف (Floor3D.jsx → floorStyle.levelCm): هر اتاق می‌تواند ±۲۰ سانت بالاتر/پایین‌تر از سطحِ
// مبنای خانه (y=۰) باشد
function roomFloorLevel(rect) {
  return resolveFloorStyle(rect).levelCm;
}

// ارتفاعِ کف در یک نقطه‌ی نقشه (سانتی‌متر) — کفِ اتاقی که نقطه داخلش است؛ بیرون از اتاق‌ها = سطحِ مبنا
function floorLevelAt(rects, x, z) {
  const room = (rects || []).find((r) => spaceContains(r, x, z));
  return room ? roomFloorLevel(room) : 0;
}

// کفِ دو طرفِ یک نقطه روی دیوار: سمتِ a رو به +Z محلیِ دیوار = بردارِ (-uy, ux) در نقشه
const SIDE_PROBE_CM = 20;
// اتاقِ دو طرفِ یک نقطه روی دیوار (همان قراردادِ wallSideLevels) — برای جنسِ پله (کفِ اتاقِ بالاتر)
function wallSideRooms(rects, wall, dir, along, thickness) {
  const px = wall.x1 + dir.ux * along;
  const pz = wall.y1 + dir.uy * along;
  const off = thickness / 2 + SIDE_PROBE_CM;
  const at = (x, z) =>
    (rects || []).find((r) => spaceContains(r, x, z)) || null;
  return { a: at(px - dir.uy * off, pz + dir.ux * off), b: at(px + dir.uy * off, pz - dir.ux * off) };
}

// پنجره: ارتفاع از کف از «کفِ اتاقِ خودش» حساب می‌شود — کفِ بالاترِ اتاق‌های دو طرف (دیوارِ بیرونی فقط
// یک اتاق دارد؛ بیرونِ ساختمان حساب نمی‌شود)
function windowBaseLevel(rects, wall, dir, along, thickness) {
  const rooms = wallSideRooms(rects, wall, dir, along, thickness);
  const present = [rooms.a, rooms.b].filter(Boolean).map(roomFloorLevel);
  return present.length ? Math.max(...present) : 0;
}

function wallSideLevels(rects, wall, dir, along, thickness) {
  const px = wall.x1 + dir.ux * along;
  const pz = wall.y1 + dir.uy * along;
  const off = thickness / 2 + SIDE_PROBE_CM;
  return {
    a: floorLevelAt(rects, px - dir.uy * off, pz + dir.ux * off),
    b: floorLevelAt(rects, px + dir.uy * off, pz - dir.ux * off),
  };
}

function getWallDirection(wall) {
  const dx = wall.x2 - wall.x1;
  const dy = wall.y2 - wall.y1;
  const len = Math.hypot(dx, dy);
  if (len === 0) return null;
  return { ux: dx / len, uy: dy / len, len };
}

// فاصله (روی طول دیوار، بر حسب سانتی‌متر) یک نقطه‌ی جهانی، وقتی روی محور خودِ همین دیوار تصویر شود —
// جهت‌دار، پس اگر دیوار مشترکِ هم‌پوشان با جهتِ برعکس تعریف شده باشد هم نتیجه‌ی درستی می‌دهد
function projectPointToWallDistance(wall, point) {
  const dir = getWallDirection(wall);
  if (!dir) return 0;
  return (point.x - wall.x1) * dir.ux + (point.y - wall.y1) * dir.uy;
}

// فاصله‌ی عمودیِ یک نقطه تا خطِ (نامحدود) روی همین دیوار
function perpendicularDistanceToLine(wall, point) {
  const dir = getWallDirection(wall);
  if (!dir) return Infinity;
  return Math.abs(dir.ux * (point.y - wall.y1) - dir.uy * (point.x - wall.x1));
}

// دو دیوار «دیوار مشترک» محسوب می‌شوند اگر روی همان خط باشند (تقریباً موازی + بدون فاصله‌ی عمودی)
// و بازه‌شان روی آن خط هم‌پوشانی داشته باشد — نه لزوماً با دو سرِ کاملاً یکسان، چون دو اتاق کنار هم
// (مثلاً اتاق‌خواب و پذیرایی) معمولاً طول دیوار متفاوتی دارند و فقط بخشی از دیوارشان مشترک است
function wallsShareLine(a, b) {
  const da = getWallDirection(a);
  const db = getWallDirection(b);
  if (!da || !db) return false;
  const cross = Math.abs(da.ux * db.uy - da.uy * db.ux);
  if (cross > 0.02) return false; // تقریباً موازی (اختلاف کمتر از ~1.2 درجه)
  if (perpendicularDistanceToLine(a, { x: b.x1, y: b.y1 }) > WALL_OVERLAP_TOLERANCE_CM) return false;
  if (perpendicularDistanceToLine(a, { x: b.x2, y: b.y2 }) > WALL_OVERLAP_TOLERANCE_CM) return false;
  const bStartOnA = projectPointToWallDistance(a, { x: b.x1, y: b.y1 });
  const bEndOnA = projectPointToWallDistance(a, { x: b.x2, y: b.y2 });
  const lo = Math.min(bStartOnA, bEndOnA);
  const hi = Math.max(bStartOnA, bEndOnA);
  return hi > 0 && lo < da.len;
}

// فهرست تکه‌های محلی یک دیوار (بر حسب فاصله از ابتدای خودِ دیوار)، با در نظر گرفتن در/پنجره/بازشوهایی
// که یا مستقیماً روی همین دیوار ثبت شده‌اند، یا روی یک «دیوار مشترکِ» هم‌پوشان با آن — همان منطقی که
// هم برای رندر بصری استفاده می‌شود و هم (پایین‌تر) برای برخورد در حالت راه‌رفتن اول‌شخص
function computeWallLocalSegments(wall, allWalls, doors, windows, openings, doorWidth, windowWidth, openingWidth) {
  const dir = getWallDirection(wall);
  if (!dir) return [];
  const wallLength = dir.len;

  const siblingIds = [wall.id];
  allWalls.forEach((other) => {
    if (other.id !== wall.id && wallsShareLine(wall, other)) siblingIds.push(other.id);
  });

  const items = [];
  // itemWidth پیش‌فرضِ کلِ آن نوع است؛ ولی برای «در» یا «پنجره»، اگر خودِ آن یک عرضِ اختصاصی
  // (style.widthCm) داشته باشد (از پنلِ ادیتِ در/پنجره)، همان اولویت دارد — برای همین اندازه‌ی
  // بازشوی برش‌خورده از دیوار، دقیقاً همان چیزی می‌شود که کاربر برای این المانِ خاص انتخاب کرده،
  // نه یک اندازه‌ی مشترکِ ثابت
  const collect = (list, itemWidth, kind) => {
    list.forEach((entry) => {
      if (!siblingIds.includes(entry.wallId)) return;
      const ownerWall = entry.wallId === wall.id ? wall : allWalls.find((w) => w.id === entry.wallId);
      if (!ownerWall) return;
      const width = entry.style?.widthCm ? entry.style.widthCm : itemWidth;
      const localSpan = getWallItemSpan(ownerWall, entry.ratio, width);
      const odir = getWallDirection(ownerWall);
      if (!odir) return;
      const p1 = { x: ownerWall.x1 + odir.ux * localSpan.start, y: ownerWall.y1 + odir.uy * localSpan.start };
      const p2 = { x: ownerWall.x1 + odir.ux * localSpan.end, y: ownerWall.y1 + odir.uy * localSpan.end };
      const d1 = projectPointToWallDistance(wall, p1);
      const d2 = projectPointToWallDistance(wall, p2);
      const start = Math.max(0, Math.min(d1, d2));
      const end = Math.min(wallLength, Math.max(d1, d2));
      if (end > start) items.push({ start, end, kind, id: entry.id });
    });
  };

  collect(doors, doorWidth, 'door');
  collect(windows, windowWidth, 'window');
  collect(openings, openingWidth, 'opening');

  return buildWallSegments(wallLength, items);
}

// فهرست پاره‌خط‌های «مسدودکننده» در کل ساختمان (دیوار توپر + پنجره — چون شیشه هم مانع عبور است)، به
// مختصات جهانی، برای برخورد در حالت راه‌رفتن. در/بازشو عمداً مسدودکننده نیستند: کاربر باید بتواند از
// در و بازشو رد شود
// openDoorIds: درِ بسته هم مثلِ دیوار جلوی راه را می‌گیرد (باید بازش کرد)؛ درِ باز و بازشوی بدونِ در، نه
function computeBlockingSegmentsWorld(walls, doors, windows, openings, doorWidth, windowWidth, openingWidth, openDoorIds) {
  const result = [];
  walls.forEach((wall) => {
    const dir = getWallDirection(wall);
    if (!dir) return;
    const segments = computeWallLocalSegments(wall, walls, doors, windows, openings, doorWidth, windowWidth, openingWidth);
    segments.forEach((seg) => {
      const closedDoor = seg.kind === 'door' && !(openDoorIds && openDoorIds.has(seg.id));
      if (seg.kind !== 'solid' && seg.kind !== 'window' && !closedDoor) return;
      result.push({
        x1: wall.x1 + dir.ux * seg.start,
        y1: wall.y1 + dir.uy * seg.start,
        x2: wall.x1 + dir.ux * seg.end,
        y2: wall.y1 + dir.uy * seg.end,
      });
    });
  });
  return result;
}

// دیوارِ مشترکِ دو اتاق = دو دیوارِ رویِ‌هم. اگر هر دو رویه‌هایشان را بسازند، دو سطح دقیقاً در یک صفحه‌اند و
// تصویر «می‌لرزد» (z-fighting) — مخصوصاً وقتی یک سمت پوشش/رنگِ جدا گرفته. این‌جا برای هر سمتِ دیوار، بازه‌هایی
// پیدا می‌شود که باید رویه‌ی دیوارِ دیگر دیده شود: رویه‌ی هر سمت مالِ دیوارِ اتاقِ همان سمت است (همان دیواری که
// کاربر از داخلِ آن اتاق انتخاب و رنگ می‌کند). اگر هیچ‌کدام مالِ آن اتاق نبود، دیوارِ با شناسه‌ی کوچک‌تر می‌ماند
function compareWallIds(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}
function computeHiddenFaceRanges(wall, allWalls, rects, thickness) {
  const out = { a: [], b: [] };
  const dir = getWallDirection(wall);
  if (!dir) return out;
  allWalls.forEach((other) => {
    if (other.id === wall.id || !wallsShareLine(wall, other)) return;
    const s1 = projectPointToWallDistance(wall, { x: other.x1, y: other.y1 });
    const s2 = projectPointToWallDistance(wall, { x: other.x2, y: other.y2 });
    const lo = Math.max(0, Math.min(s1, s2));
    const hi = Math.min(dir.len, Math.max(s1, s2));
    if (hi - lo < 1) return;
    const rooms = wallSideRooms(rects, wall, dir, (lo + hi) / 2, thickness);
    ['a', 'b'].forEach((side) => {
      const ids = (rooms[side] && rooms[side].wallIds) || [];
      // فضای دستی هر دو دیوارِ کنارش را «مالِ خود» می‌داند — آن‌وقت همان قاعده‌ی شناسه‌ی کوچک‌تر
      const mine = ids.includes(wall.id);
      const theirs = ids.includes(other.id);
      let hide;
      if (mine && !theirs) hide = false;
      else if (theirs && !mine) hide = true;
      else hide = compareWallIds(wall.id, other.id) > 0;
      if (hide) out[side].push([lo, hi]);
    });
  });
  return out;
}

// موقعیتِ جهانیِ سه‌بعدیِ (x, y, z) مرکزِ هر در — برای نشانه‌ی «دست» در حالت اول‌شخص: هر فریم زاویه‌ی
// واقعیِ نگاهِ کاربر (یاو + پیچ، یعنی همان‌جهتی که واقعاً وسطِ صفحه‌ی نمایش است) با این نقطه‌ها سنجیده
// می‌شود، نه فقط فاصله؛ y هم لازم است چون اگر فقط x/z باشد، نگاه‌کردن به بالا/پایین در محاسبه اثر
// نمی‌گذارد و درِ وسطِ صفحه درست تشخیص داده نمی‌شود (پایین‌تر، در FirstPersonRig)
function computeDoorWorldSpots(walls, doors, windows, openings, doorWidth, windowWidth, openingWidth) {
  const spots = [];
  walls.forEach((wall) => {
    const dir = getWallDirection(wall);
    if (!dir) return;
    const segments = computeWallLocalSegments(wall, walls, doors, windows, openings, doorWidth, windowWidth, openingWidth);
    segments.forEach((seg) => {
      if (seg.kind !== 'door') return;
      const centerAlong = (seg.start + seg.end) / 2;
      const doorData = doors.find((d) => d.id === seg.id);
      const doorHeightCm = doorData?.style?.heightCm || DOOR_HEIGHT_CM;
      spots.push({
        id: seg.id,
        kind: 'door',
        x: wall.x1 + dir.ux * centerAlong,
        y: doorHeightCm / 2,
        z: wall.y1 + dir.uy * centerAlong,
      });
    });
  });
  return spots;
}

// همانِ منطقِ بالا، برای پنجره — با یک تفاوت: پنجره‌ی «ثابت» (fixed) اصلاً باز نمی‌شود، پس نشانه‌ی
// دست هم برایش لازم نیست و از لیستِ نقطه‌های قابل‌تعامل کنار گذاشته می‌شود
function computeWindowWorldSpots(walls, doors, windows, openings, doorWidth, windowWidth, openingWidth) {
  const spots = [];
  walls.forEach((wall) => {
    const dir = getWallDirection(wall);
    if (!dir) return;
    const segments = computeWallLocalSegments(wall, walls, doors, windows, openings, doorWidth, windowWidth, openingWidth);
    segments.forEach((seg) => {
      if (seg.kind !== 'window') return;
      const winData = windows.find((w) => w.id === seg.id);
      if (!winData) return;
      const type = winData.style?.type || DEFAULT_WINDOW_STYLE.type;
      if (type === 'fixed') return;
      // دقیقاً همان کلمپِ ارتفاعی که Wall3D موقعِ رندرِ خودِ پنجره اعمال می‌کند (پایین‌تر، شاخه‌ی
      // seg.kind === 'window') — وگرنه برای پنجره‌ای که کاربر ارتفاع/ارتفاعِ‌از‌کفش را طوری تنظیم
      // کرده که بدونِ کلمپ از سقف رد می‌شد، نقطه‌ی نشانه‌ی دست چند سانتی بالاتر از مرکزِ واقعیِ
      // پنجره‌ی رندرشده می‌افتاد و در حالتِ اول‌شخص، نشانه دقیقاً روی پنجره نمی‌نشست
      const { sillCm, headerCm } = getWindowVerticalRange(winData);
      const clampedHeader = Math.min(headerCm, WALL_HEIGHT_CM - 1);
      const clampedSill = Math.max(0, Math.min(sillCm, clampedHeader - 10));
      const centerAlong = (seg.start + seg.end) / 2;
      spots.push({
        id: seg.id,
        kind: 'window',
        x: wall.x1 + dir.ux * centerAlong,
        y: (clampedSill + clampedHeader) / 2,
        z: wall.y1 + dir.uy * centerAlong,
      });
    });
  });
  return spots;
}

// مرکزِ هر در/پنجره/بازشو روی نقشه (سانتی‌متر، x و z) — برای «روشناییِ اتاق» (roomLighting.jsx): این‌که
// کدام در/پنجره/بازشو روی دیوارهای کدام اتاق است. برخلافِ computeWindowWorldSpots، پنجره‌ی ثابت هم حساب
// می‌شود (نور را رد می‌کند). هر مورد فقط یک بار (دیوارهای هم‌خط ممکن است یک مورد را دو بار ببینند)
function computeWallItemCenters(walls, doors, windows, openings, doorWidth, windowWidth, openingWidth) {
  const out = { doors: [], windows: [], openings: [] };
  const seen = new Set();
  walls.forEach((wall) => {
    const dir = getWallDirection(wall);
    if (!dir) return;
    const segments = computeWallLocalSegments(wall, walls, doors, windows, openings, doorWidth, windowWidth, openingWidth);
    segments.forEach((seg) => {
      if (seg.kind !== 'door' && seg.kind !== 'window' && seg.kind !== 'opening') return;
      const key = `${seg.kind}:${seg.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      const centerAlong = (seg.start + seg.end) / 2;
      const entry = {
        id: seg.id,
        x: wall.x1 + dir.ux * centerAlong,
        z: wall.y1 + dir.uy * centerAlong,
      };
      if (seg.kind === 'window') {
        // مساحتِ واقعیِ پنجره (عرضِ بازشو × ارتفاعِ کلمپ‌شده، همان که رندر می‌شود) — روشناییِ پنجره‌ی باز
        // به نسبتِ همین مساحت است. پنجره‌ی ثابت باز نمی‌شود، پس مساحتش صفر حساب می‌شود
        const winData = windows.find((w) => w.id === seg.id);
        const { sillCm, headerCm } = getWindowVerticalRange(winData);
        const top = Math.min(headerCm, WALL_HEIGHT_CM - 1);
        const bottom = Math.max(0, Math.min(sillCm, top - 10));
        const isFixed = resolveWindowStyle(winData).type === 'fixed';
        entry.areaCm2 = isFixed ? 0 : (seg.end - seg.start) * Math.max(0, top - bottom);
      }
      out[`${seg.kind}s`].push(entry);
    });
  });
  return out;
}

// در/پنجره/بازشوهای هر دیوار، در امتدادِ «همان» دیوار (سانتی‌متر) با ارتفاعِ واقعی‌شان — برای عینکِ مهندسی
// (عرض/ارتفاع/ارتفاع از کف/فاصله تا گوشه). خروجی: Map(wallId → [{ kind, id, start, end, bottom, top }])
function computeWallItemsByWall(walls, doors, windows, openings, doorWidth, windowWidth, openingWidth, rects, thickness = 0) {
  const map = new Map();
  walls.forEach((wall) => {
    const dir = getWallDirection(wall);
    const segments = computeWallLocalSegments(wall, walls, doors, windows, openings, doorWidth, windowWidth, openingWidth);
    const items = [];
    segments.forEach((seg) => {
      if (seg.kind === 'door') {
        const doorData = doors.find((d) => d.id === seg.id);
        // کلِ در (با ارتفاعِ کاملش) روی کفِ بالاترِ دو طرف می‌نشیند (آستانه)
        const lv = dir ? wallSideLevels(rects, wall, dir, (seg.start + seg.end) / 2, thickness) : { a: 0, b: 0 };
        const bottom = lv.a !== lv.b ? Math.max(lv.a, lv.b) : Math.max(0, lv.a);
        const top = Math.min(WALL_HEIGHT_CM, bottom + (doorData?.style?.heightCm || DOOR_HEIGHT_CM));
        items.push({ kind: 'door', id: seg.id, start: seg.start, end: seg.end, bottom, top, levels: lv });
      } else if (seg.kind === 'window') {
        const winData = windows.find((w) => w.id === seg.id);
        // همان کلمپِ ارتفاعی که رندرِ خودِ پنجره اعمال می‌کند
        const { sillCm, headerCm } = getWindowVerticalRange(winData);
        const base = dir ? windowBaseLevel(rects, wall, dir, (seg.start + seg.end) / 2, thickness) : 0;
        const top = Math.min(headerCm + base, WALL_HEIGHT_CM - 1);
        const bottom = Math.max(base, Math.min(sillCm + base, top - 10));
        items.push({ kind: 'window', id: seg.id, start: seg.start, end: seg.end, bottom, top });
      } else if (seg.kind === 'opening') {
        const lv = dir ? wallSideLevels(rects, wall, dir, (seg.start + seg.end) / 2, thickness) : { a: 0, b: 0 };
        items.push({
          kind: 'opening',
          id: seg.id,
          start: seg.start,
          end: seg.end,
          bottom: lv.a !== lv.b ? Math.max(lv.a, lv.b) : Math.max(0, lv.a),
          top: WALL_HEIGHT_CM,
          levels: lv,
        });
      }
    });
    if (items.length) map.set(wall.id, items);
  });
  return map;
}

function pointSegmentDistanceCm(px, pz, x1, z1, x2, z2) {
  const dx = x2 - x1;
  const dz = z2 - z1;
  const lenSq = dx * dx + dz * dz;
  let t = lenSq === 0 ? 0 : ((px - x1) * dx + (pz - z1) * dz) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = x1 + t * dx;
  const cz = z1 + t * dz;
  return Math.hypot(px - cx, pz - cz);
}

function isWalkPositionBlocked(x, z, blockingSegments) {
  return blockingSegments.some(
    (seg) => pointSegmentDistanceCm(x, z, seg.x1, seg.y1, seg.x2, seg.y2) < PLAYER_RADIUS_CM
  );
}

// برخورد ساده با «سرخوردن» روی محورها: اگر حرکت قطری به دیوار می‌خورد، تک‌تک محور X و Z را امتحان
// می‌کند تا کاربر کنار دیوار بلغزد، به‌جای این‌که کاملاً متوقف شود
function resolveWalkMove(pos, moveX, moveZ, blockingSegments) {
  const full = { x: pos.x + moveX, z: pos.z + moveZ };
  if (!isWalkPositionBlocked(full.x, full.z, blockingSegments)) return full;

  const slideX = { x: pos.x + moveX, z: pos.z };
  if (!isWalkPositionBlocked(slideX.x, slideX.z, blockingSegments)) return slideX;

  const slideZ = { x: pos.x, z: pos.z + moveZ };
  if (!isWalkPositionBlocked(slideZ.x, slideZ.z, blockingSegments)) return slideZ;

  return pos;
}

// زیرسازیِ ساده‌ی کف/سقف به اندازه‌ی کلِ خانه (spaces.computeSpaces با footprint) — پشتِ کف و سقفِ اتاق‌ها
function FootprintFill({ pieces, y, up, color }) {
  const geometry = useMemo(() => {
    if (!pieces || !pieces.length) return null;
    return buildSurfaceGeometry(pieces, piecesFrame(pieces), y, up);
  }, [pieces, y, up]);
  useEffect(() => () => geometry && geometry.dispose(), [geometry]);
  if (!geometry) return null;
  return (
    <mesh geometry={geometry} raycast={() => null} userData={{ khNoRoomLight: true }}>
      <meshStandardMaterial color={color} roughness={0.95} metalness={0} />
    </mesh>
  );
}

function computeSceneBounds(walls) {
  if (!walls.length) {
    return { centerX: 0, centerY: 0, sizeX: 600, sizeY: 600 };
  }
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  walls.forEach((w) => {
    minX = Math.min(minX, w.x1, w.x2);
    maxX = Math.max(maxX, w.x1, w.x2);
    minY = Math.min(minY, w.y1, w.y2);
    maxY = Math.max(maxY, w.y1, w.y2);
  });
  return {
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
    sizeX: Math.max(1, maxX - minX),
    sizeY: Math.max(1, maxY - minY),
  };
}

function Wall3D({
  wall,
  allWalls,
  doors,
  windows,
  openings,
  wallThickness,
  doorWidth,
  windowWidth,
  openingWidth,
  onSelectWall,
  highlightSide,
  openDoorIds,
  selectedDoorId,
  onSelectDoor,
  openWindowIds,
  selectedWindowId,
  onSelectWindow,
  selectedOpeningId,
  onSelectOpening,
  interiorAo,
  roomGroupRects,
  minLevel = 0,
}) {
  const dx = wall.x2 - wall.x1;
  const dy = wall.y2 - wall.y1;
  const wallLength = Math.hypot(dx, dy);

  // جهت دیوار به‌صورت بردار سه‌بعدی (X: افق نقشه، Z: عمق نقشه، Y: ارتفاع) — چرخش دیوار با
  // setFromUnitVectors محاسبه می‌شود تا از محاسبه دستی زاویه (atan2) و ریسک اشتباه در علامت آن دوری شود
  const dir = useMemo(() => {
    if (wallLength === 0) return new THREE.Vector3(1, 0, 0);
    return new THREE.Vector3(dx, 0, dy).normalize();
  }, [dx, dy, wallLength]);

  const quaternion = useMemo(
    () => new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(1, 0, 0), dir),
    [dir]
  );

  const segments = useMemo(
    () => computeWallLocalSegments(wall, allWalls, doors, windows, openings, doorWidth, windowWidth, openingWidth),
    [wall, allWalls, doors, windows, openings, doorWidth, windowWidth, openingWidth]
  );

  // رویه‌هایی که دیوارِ مشترکِ هم‌پوشان می‌سازد (جلوگیری از لرزشِ دو سطحِ رویِ‌هم)
  const hiddenFaces = useMemo(
    () => computeHiddenFaceRanges(wall, allWalls, roomGroupRects, wallThickness),
    [wall, allWalls, roomGroupRects, wallThickness]
  );

  // این return همیشه بعد از تمام Hook‌های بالا اجرا می‌شود (رعایت قانون Hook‌های React)
  if (wallLength === 0) return null;

  // تنظیماتِ دو رویه‌ی این دیوار (sides.a و sides.b) — اگر کاربر هنوز چیزی تنظیم نکرده باشد، یا
  // پلانِ قدیمی باشد (رنگ/براقیت/قرنیزِ مشترکِ قبلی)، resolveWallSides این را هندل می‌کند
  const wallSides = resolveWallSides(wall);
  const openingProtrude = openingProtrusionCm(wallSides);

  // انتخابِ دیوار حالا در سطحِ خودِ رویه (element3D/Wall3D.jsx → WallFace) اتفاق می‌افتد، نه این‌جا —
  // چون هر رویه مشِ کاملاً جداگانه‌ای است و می‌داند خودش کدام سمت است (faceSign). این‌جا فقط شناسه‌ی
  // خودِ دیوار را به آن callback اضافه می‌کنیم تا بالادست بداند کدام دیوار + کدام سمتش انتخاب شده
  const handleSelectSide = (side) => {
    if (onSelectWall) onSelectWall(wall.id, side);
  };

  // رندرِ هر تکه با «المان‌های» جدا (WallSegment/Door/Window از پوشه‌ی element3D) به‌جای ساختن
  // مستقیمِ مش این‌جا — هدف این است که بعداً بشود روی خودِ در/پنجره/دیوار (رنگ، حالت، جزییات بیشتر)
  // مستقل کار کرد بدون این‌که این تابع (که فقط مسئولِ تقسیم‌بندیِ طولِ دیوار است) عوض شود
  return (
    <group position={[wall.x1, 0, wall.y1]} quaternion={quaternion}>
      {/* نوار برجسته‌ی آبی کف دیوار، فقط سمتِ روی همان رویه‌ای که انتخاب شده — نشانه‌ی بصری انتخاب که
          هم دیوار و هم سمتِ درستش را از هر زاویه‌ی دیدِ اوربیت مشخص می‌کند */}
      {highlightSide && (
        <mesh
          position={[
            wallLength / 2,
            1,
            highlightSide === 'a' ? wallThickness / 2 + 4 : -(wallThickness / 2 + 4),
          ]}
        >
          <boxGeometry args={[wallLength, 2, 3]} />
          <meshBasicMaterial color="#0a66c2" />
        </mesh>
      )}
      {segments.map((seg, index) => {
        const segLength = seg.end - seg.start;
        if (segLength <= 0) return null;
        const centerAlong = (seg.start + seg.end) / 2;
        // کفِ دو طرفِ همین تکه (اختلافِ سطح) — برای قرنیز/سایه/پوششِ پایینی و آستانه‌ی در/بازشو
        const levels = wallSideLevels(roomGroupRects, wall, { ux: dir.x, uy: dir.z }, centerAlong, wallThickness);
        const upper = Math.max(levels.a, levels.b);
        const hasStep = levels.a !== levels.b || minLevel < 0;
        // جنسِ پله = کفِ اتاقِ بالاتر (اگر هم‌سطح‌اند، اتاقِ سمتِ a)
        const stepFloorStyle = hasStep
          ? (() => {
              const rooms = wallSideRooms(roomGroupRects, wall, { ux: dir.x, uy: dir.z }, centerAlong, wallThickness);
              const idx = levels.b > levels.a ? 'b' : 'a';
              const room = rooms[idx] || rooms.a || rooms.b;
              const roomIndex = room ? roomGroupRects.indexOf(room) : -1;
              return room ? resolveFloorStyle(room, FLOOR_PALETTE[roomIndex % FLOOR_PALETTE.length]) : null;
            })()
          : null;

        // بازشو (opening): از کف تا سقف کاملاً باز است — فقط اگر اختلافِ سطح باشد، یک آستانه‌ی سنگی (پله)
        // دیوارِ مشترکِ دو اتاق دو دیوارِ رویِ‌هم است و هر دو، در/پنجره/بازشوی روی خط را می‌بُرند؛ ولی خودِ
        // در/پنجره/آستانه فقط روی دیوارِ «صاحبش» (wallId) ساخته می‌شود — قبلاً روی هر دو ساخته می‌شد، یعنی
        // هر درِ داخلی دو بار (دو برابر مش و draw call) رندر می‌شد و حرکت روی موبایل سنگین/پرشی می‌شد
        if (seg.kind === 'opening') {
          // بازشو: دهانه‌ی ۲۴۰ سانتی، بالایش نعل‌درگاه (روی همه‌ی دیوارهای رویِ‌هم، مثلِ بالای در)، و فقط روی
          // دیوارِ «صاحبش» آستانه (اگر اختلافِ سطح هست) و چارچوب. style.frame: 'white' | 'wood' | 'plaster'
          const openingData = openings.find((o) => o.id === seg.id);
          const ownsOpening = !openingData || openingData.wallId === wall.id;
          const openBase = hasStep || upper !== 0 ? upper + 0.5 : 0;
          const openTop = Math.min(WALL_HEIGHT_CM - 1, openBase + (openingData?.style?.heightCm || OPENING_HEIGHT_CM));
          return (
            <group key={index}>
              <WallSegment
                length={segLength}
                height={WALL_HEIGHT_CM - openTop}
                thickness={wallThickness}
                centerAlong={centerAlong}
                bottom={openTop}
                floorLevels={levels}
                sides={wallSides}
                hidden={hiddenFaces}
                onSelectSide={handleSelectSide}
                aoTop={interiorAo}
              />
              {ownsOpening && (
                <ThresholdBlock
                  length={segLength}
                  thickness={wallThickness}
                  centerAlong={centerAlong}
                  bottom={minLevel}
                  levels={levels}
                  floorStyle={stepFloorStyle}
                />
              )}
              {ownsOpening && (
                <OpeningFrame
                  width={segLength}
                  height={openTop - openBase}
                  thickness={wallThickness}
                  centerAlong={centerAlong}
                  base={openBase}
                  style={openingData?.style?.frame || 'white'}
                  isSelected={!!(openingData && selectedOpeningId === openingData.id)}
                  onSelect={
                    openingData && onSelectOpening
                      ? () => onSelectOpening(openingData.id)
                      : undefined
                  }
                />
              )}
            </group>
          );
        }

        if (seg.kind === 'solid') {
          return (
            <WallSegment
              key={index}
              length={segLength}
              height={WALL_HEIGHT_CM - minLevel}
              thickness={wallThickness}
              centerAlong={centerAlong}
              bottom={minLevel}
              floorLevels={levels}
              sides={wallSides}
              hidden={hiddenFaces}
              onSelectSide={handleSelectSide}
              aoTop={interiorAo}
              aoStart={interiorAo && seg.start <= 0.5}
              aoEnd={interiorAo && seg.end >= wallLength - 0.5}
            />
          );
        }

        if (seg.kind === 'door') {
          // خودِ door را از روی id پیدا می‌کنیم (نه این‌که فقط عرض/ارتفاع را جدا پاس بدهیم)، چون
          // style کاملِ در (نوع/رنگ/دستگیره) و ارتفاعِ اختصاصی‌اش (اگر تنظیم شده باشد) همه از همین
          // شیء می‌آیند؛ اگر به هر دلیلی پیدا نشد (نباید پیش بیاید)، به رفتارِ پیش‌فرضِ قبلی برمی‌گردیم
          const doorData = doors.find((d) => d.id === seg.id) || null;
          const doorHeightCm = doorData?.style?.heightCm || DOOR_HEIGHT_CM;
          const ownsDoor = !doorData || doorData.wallId === wall.id;
          // کلِ در (بازشو، چارچوب، زهوار، لنگه) روی کفِ بالاترِ دو طرف می‌نشیند — ارتفاعِ در از همان کف است،
          // پس وقتی کفِ اتاق بالا/پایین می‌رود، در هم با آن بالا/پایین می‌رود. از سمتِ پایین‌تر، زهوار «روی
          // پله» می‌ایستد (مثلِ اجرای واقعی): پله از دو طرف به‌اندازه‌ی زهوار پهن‌تر از بازشو است تا زهوار
          // کاملاً رویش بنشیند — قبلاً زهوار تا کفِ پایین ادامه داشت و دماغه‌ی پله در آن فرو می‌رفت
          // (باگِ قبلی: وقتی هر دو طرف به یک اندازه بالاتر بودند، «اختلافی» دیده نمی‌شد و در روی سطحِ مبنا
          // می‌ماند و چارچوب در کفِ بالاآمده فرو می‌رفت — حالا هر کفِ غیرِ صفر هم حساب می‌شود)
          const doorBase = hasStep || upper !== 0 ? upper + 0.5 : 0;
          const doorTop = Math.min(WALL_HEIGHT_CM - 1, doorBase + doorHeightCm);
          return (
            <group key={index}>
              {/* سرتاسر بالای در — تکه‌ی دیوار بین بالای در و سقف */}
              <WallSegment
                length={segLength}
                height={WALL_HEIGHT_CM - doorTop}
                thickness={wallThickness}
                centerAlong={centerAlong}
                bottom={doorTop}
                floorLevels={levels}
                sides={wallSides}
                hidden={hiddenFaces}
                onSelectSide={handleSelectSide}
                aoTop={interiorAo}
              />
              {/* آستانه: از پایین‌ترین کفِ خانه تا کفِ بالاترِ دو طرف (فقط وقتی اختلافِ سطح هست) */}
              {ownsDoor && (
              <ThresholdBlock
                length={segLength + DOOR_CASING_OVERLAP_CM * 2}
                thickness={wallThickness}
                centerAlong={centerAlong}
                bottom={minLevel}
                levels={levels}
                floorStyle={stepFloorStyle}
              />
              )}
              {ownsDoor && (
              <group position={[0, doorBase, 0]}>
              <Door
                width={segLength}
                height={doorTop - doorBase}
                thickness={wallThickness}
                centerAlong={centerAlong}
                protrude={openingProtrude}
                style={resolveDoorStyle(doorData)}
                facing={doorData?.facing}
                hinge={doorData?.hinge}
                isOpen={!!(doorData && openDoorIds?.has(doorData.id))}
                isSelected={!!(doorData && selectedDoorId === doorData.id)}
                onSelect={() => {
                  if (doorData && onSelectDoor) onSelectDoor(doorData.id);
                }}
              />
              </group>
              )}
            </group>
          );
        }

        if (seg.kind === 'window') {
          // مثلِ در: خودِ window را از روی id پیدا می‌کنیم تا style کامل (نوع/رنگ/ابعاد/ارتفاعِ‌از‌کف)
          // از همین شیء بیاید، نه مقادیرِ سراسریِ ثابت
          const winData = windows.find((w) => w.id === seg.id) || null;
          const ownsWindow = !winData || winData.wallId === wall.id;
          const { sillCm, headerCm } = getWindowVerticalRange(winData);
          // ارتفاع از کفِ پنجره از کفِ اتاقِ خودش (اختلافِ سطح) — با بالا/پایین رفتنِ کف، پنجره هم جابه‌جا می‌شود
          const winBase = windowBaseLevel(roomGroupRects, wall, { ux: dir.x, uy: dir.z }, centerAlong, wallThickness);
          // یک محافظِ ساده تا اگر کاربر عددهای افراطی انتخاب کرد (مثلاً ارتفاعِ‌از‌کف خیلی بالا)، حداقل
          // تکه‌های دیوارِ بالا/پایین منفی نشوند و صحنه خراب نشود
          const clampedHeader = Math.min(headerCm + winBase, WALL_HEIGHT_CM - 1);
          const clampedSill = Math.max(minLevel, Math.min(sillCm + winBase, clampedHeader - 10));
          return (
            <group key={index}>
              {/* تکه‌ی دیوار زیر پنجره (از کف تا رف) */}
              <WallSegment
                length={segLength}
                height={clampedSill - minLevel}
                thickness={wallThickness}
                centerAlong={centerAlong}
                bottom={minLevel}
                floorLevels={levels}
                sides={wallSides}
                hidden={hiddenFaces}
                onSelectSide={handleSelectSide}
              />
              {/* تکه‌ی دیوار بالای پنجره (تا سقف) */}
              <WallSegment
                length={segLength}
                height={WALL_HEIGHT_CM - clampedHeader}
                thickness={wallThickness}
                centerAlong={centerAlong}
                bottom={clampedHeader}
                floorLevels={levels}
                sides={wallSides}
                hidden={hiddenFaces}
                onSelectSide={handleSelectSide}
                aoTop={interiorAo}
              />
              {ownsWindow && (
              <Window
                width={segLength}
                height={clampedHeader - clampedSill}
                thickness={wallThickness}
                centerAlong={centerAlong}
                protrude={openingProtrude}
                centerHeight={(clampedSill + clampedHeader) / 2}
                style={resolveWindowStyle(winData)}
                isOpen={!!(winData && openWindowIds?.has(winData.id))}
                isSelected={!!(winData && selectedWindowId === winData.id)}
                onSelect={() => {
                  if (winData && onSelectWindow) onSelectWindow(winData.id);
                }}
              />
              )}
            </group>
          );
        }

        return null;
      })}
    </group>
  );
}

// رنگ‌های آدمک — پوست/مو/پیراهن/شلوار/کفش، جدا از رنگ‌های معماریِ بالا نگه داشته شده چون این‌جا
// برخلاف دیوارها، سایه‌خوردنِ طبیعی (حجم گرفتن بدن زیر نور) دقیقاً همان چیزی است که باعث می‌شود
// آدمک واقعی‌تر دیده شود، پس این قطعات با meshStandardMaterial (نورپردازی‌شده) رندر می‌شوند نه Basic
const AVATAR_SKIN_COLOR = '#eab98c';
const AVATAR_HAIR_COLOR = '#3a2a1c';
const AVATAR_SHIRT_COLOR = '#e8532f';
const AVATAR_PANTS_COLOR = '#31353d';
const AVATAR_SHOE_COLOR = '#1c1d21';
const AVATAR_MARK_COLOR = '#ff5a3c';

// آدمکِ نشان‌دهنده‌ی آخرین موقعیتِ کاربر در حالت اول‌شخص — فقط وقتی در حالت دیفالت/بالا (orbit) هستیم و
// کاربر حداقل یک‌بار وارد حالت اول‌شخص شده و برگشته نمایش داده می‌شود. یک آدمِ کوچکِ کم‌وجه (کفش، پا،
// لگن، تنه، بازو، گردن، سر، مو) به‌جای کپسول/کره‌ی ساده‌ی قبلی، تا از نظر شکل واقعاً شبیه انسان باشد.
// جهتِ نگاهِ کاربر (yaw) با یک فلشِ مثلثیِ روی زمین، جلوی پا، نشان داده می‌شود — با همان قرارداد
// forward دوربین (-Math.sin(yaw), -Math.cos(yaw))، چون چرخوندن کل گروه به‌اندازه‌ی yaw دور محور Y
// دقیقاً محور -Z محلی را به همان بردار می‌برد، پس فلش را روی -Z محلی می‌گذاریم
function AvatarMarker({ spot, groundY = 0 }) {
  if (!spot) return null;

  // ابعاد بر حسب سانتی‌متر، از کف به‌سمت بالا — تقریبی و متناسب با EYE_HEIGHT_CM (قد ~۱۷۰ سانتی)
  const shoeH = 6;
  const legTopY = 90; // بالای پا / زیر لگن
  const hipTopY = 108; // بالای لگن / زیر تنه
  const torsoTopY = 150; // بالای تنه / شانه
  const neckTopY = 158;
  const headRadius = 8.5;
  const headCenterY = neckTopY + headRadius;

  const matProps = { roughness: 0.65, metalness: 0, envMapIntensity: 0 };

  return (
    <group position={[spot.x, groundY, spot.z]} rotation={[0, spot.yaw, 0]}>
      {/* حلقه‌ی روی کف — برای پیدا کردن سریع نقطه از دیدِ بالا */}
      <mesh position={[0, 0.6, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[20, 27, 32]} />
        <meshBasicMaterial color={AVATAR_MARK_COLOR} side={THREE.DoubleSide} />
      </mesh>
      {/* فلشِ جهتِ نگاه، صاف روی زمین جلوی پا (سه‌گوش، شبیه یک پیکان) */}
      <mesh position={[0, 1.2, -34]} rotation={[-Math.PI / 2, 0, 0]} scale={[1, 1.7, 1]}>
        <coneGeometry args={[7, 15, 3]} />
        <meshBasicMaterial color={AVATAR_MARK_COLOR} />
      </mesh>

      {/* دو کفش */}
      {[-9, 9].map((sideX) => (
        <mesh key={`shoe-${sideX}`} position={[sideX, shoeH / 2, 2]}>
          <boxGeometry args={[11, shoeH, 21]} />
          <meshStandardMaterial color={AVATAR_SHOE_COLOR} {...matProps} />
        </mesh>
      ))}

      {/* دو پا (کمی مخروطی، بالای زانو ضخیم‌تر) */}
      {[-9, 9].map((sideX) => (
        <mesh key={`leg-${sideX}`} position={[sideX, shoeH + (legTopY - shoeH) / 2, 0]}>
          <cylinderGeometry args={[5.4, 6.4, legTopY - shoeH, 12]} />
          <meshStandardMaterial color={AVATAR_PANTS_COLOR} {...matProps} />
        </mesh>
      ))}

      {/* لگن */}
      <mesh position={[0, (legTopY + hipTopY) / 2, 0]}>
        <boxGeometry args={[30, hipTopY - legTopY, 18]} />
        <meshStandardMaterial color={AVATAR_PANTS_COLOR} {...matProps} />
      </mesh>

      {/* تنه — کپسول عمودی، شانه‌ها کمی از لگن پهن‌تر حس می‌شوند چون بالای کپسول گرد است */}
      <mesh position={[0, (hipTopY + torsoTopY) / 2, 0]}>
        <capsuleGeometry args={[15, torsoTopY - hipTopY - 30, 8, 14]} />
        <meshStandardMaterial color={AVATAR_SHIRT_COLOR} {...matProps} />
      </mesh>

      {/* دو بازو، آویزان کنار بدن، از شانه تا کمی پایین‌تر از لگن */}
      {[-22, 22].map((sideX) => (
        <group key={`arm-${sideX}`}>
          <mesh position={[sideX, (hipTopY + 8 + (torsoTopY - 6)) / 2, 0]}>
            <cylinderGeometry args={[4.4, 5.2, torsoTopY - 6 - (hipTopY + 8), 10]} />
            <meshStandardMaterial color={AVATAR_SHIRT_COLOR} {...matProps} />
          </mesh>
          {/* دست */}
          <mesh position={[sideX, hipTopY + 6, 0]}>
            <sphereGeometry args={[5, 10, 10]} />
            <meshStandardMaterial color={AVATAR_SKIN_COLOR} {...matProps} />
          </mesh>
        </group>
      ))}

      {/* گردن */}
      <mesh position={[0, (torsoTopY + neckTopY) / 2, 0]}>
        <cylinderGeometry args={[5, 5.6, neckTopY - torsoTopY, 10]} />
        <meshStandardMaterial color={AVATAR_SKIN_COLOR} {...matProps} />
      </mesh>

      {/* سر */}
      <mesh position={[0, headCenterY, 0]}>
        <sphereGeometry args={[headRadius, 18, 18]} />
        <meshStandardMaterial color={AVATAR_SKIN_COLOR} {...matProps} />
      </mesh>
      {/* موی نیم‌کلاهی روی سر، کمی بزرگ‌تر از خودِ سر تا واقعاً رویش قرار بگیرد */}
      <mesh position={[0, headCenterY, 0]}>
        <sphereGeometry args={[headRadius * 1.06, 18, 12, 0, Math.PI * 2, 0, Math.PI * 0.52]} />
        <meshStandardMaterial color={AVATAR_HAIR_COLOR} {...matProps} />
      </mesh>
    </group>
  );
}

// کنترل‌گر حالت اول‌شخص: هر فریم موقعیت/چرخش دوربین را از روی جوی‌استیک (حرکت) و درگ لمسی (نگاه‌کردن)
// به‌روزرسانی می‌کند. جوی‌استیک و درگ لمسی هر دو بیرون از Canvas (در DOM معمولی) خوانده می‌شوند و از
// طریق ref با این کامپوننت در ارتباط‌اند تا هیچ‌کدام باعث رندر مجدد درخت React نشوند.
// posRef/yawRef از بیرون (از خودِ Create3D) پاس داده می‌شوند نه این‌که این‌جا ساخته شوند، چون وقتی کاربر
// از حالت اول‌شخص برمی‌گردد به دیفالت، دکمه‌ی بازگشت باید بتواند آخرین مقدارشان را همان لحظه بخواند تا
// آدمک را دقیقاً همان‌جا نشان دهد
// حداکثر فاصله (سانتی‌متر) و نیم‌زاویه‌ی دیدی که در آن، «در/پنجره وسطِ صفحه دیده می‌شود» محسوب
// می‌شود — عمداً روبه‌رو ایستادن جلوی خودِ در/پنجره لازم نیست؛ کافی است از هر فاصله‌ی معقولی،
// تقریباً وسطِ دیدِ کاربر باشد (زاویه‌ی نگاه با بردارِ به‌سمتِ آن کم باشد)، دقیقاً مثل نشانگرِ
// وسطِ صفحه در بازی‌ها
const DOOR_INTERACT_RANGE_CM = 650;
const DOOR_INTERACT_COS_LIMIT = Math.cos((13 * Math.PI) / 180);
// همان زاویه ولی کمی بازتر — وقتی هدفی از قبل «قفل» شده، تا وقتی نگاه از همین زاویه‌ی بازتر خارج
// نشود، آن را رها نمی‌کنیم. بدونِ این، دقیقاً روی لبه‌ی ۱۳ درجه، کوچک‌ترین لرزشِ سر باعث می‌شد
// نشانه‌ی دست هر فریم ظاهر/ناپدید شود (یا بینِ دو نامزدِ نزدیک عوض شود) و لمس گاهی درست وقتی
// بیفتد که نشانه لحظه‌ای غیب شده یا هدف عوض شده — همان چیزی که به‌نظر می‌رسید «دقیق باز و بسته
// نمی‌شود»
const DOOR_INTERACT_COS_RELEASE = Math.cos((17 * Math.PI) / 180);
// برای رقابتِ بینِ دو نشانه‌ی نزدیک‌به‌هم (مثلاً یک در و یک پنجره تقریباً هم‌مرکز): هدفِ فعلی را
// فقط وقتی به نفعِ رقیبِ تازه عوض می‌کنیم که رقیب واقعاً بهتر باشد، نه با کوچک‌ترین اختلاف
const DOOR_INTERACT_SWITCH_MARGIN = 0.01;

// آستانه‌ی «پیچِ نگاه» (radians) برای تشخیصِ نگاه‌کردن به سقف — برخلافِ در/پنجره که یک نقطه‌ی مشخص در
// فضا هستند و معیارشان «مرکزیت» است، سقف یک صفحه‌ی بزرگِ بالای سرِ کل اتاق است، پس معیارش فقط «به
// اندازه‌ی کافی سر را بالا گرفتن» است. هیسترزیس دقیقاً همان الگوی DOOR_INTERACT_COS_LIMIT/RELEASE:
// آستانه‌ی ورود بالاتر از آستانه‌ی خروج، تا کمی بالا/پایین رفتنِ نگاه دورِ لبه باعثِ چشمک‌زدنِ نشانه نشود
const CEILING_LOOK_PITCH_ENTER = (48 * Math.PI) / 180;
const CEILING_LOOK_PITCH_RELEASE = (36 * Math.PI) / 180;

function FirstPersonRig({
  active,
  bounds,
  roomGroupRects,
  blockingSegments,
  interactiveSpots,
  onNearbyTargetChange,
  joystickRef,
  lookDragRef,
  posRef,
  yawRef,
  pitchRef: sharedPitchRef,
  onFootstep,
}) {
  const { camera } = useThree();
  // زاویه‌ی نگاهِ بالا/پایین — اگر از بیرون داده شود (برای عینکِ مهندسی: «نگاه به کف»)، همان استفاده می‌شود
  const localPitchRef = useRef(0);
  const pitchRef = sharedPitchRef || localPitchRef;
  // وضعیتِ راه رفتن: سرعتِ فعلی (سانتی‌متر/ثانیه)، فازِ قدم‌ها و شدتِ حرکتِ سر، و فنرِ برگشتِ نگاه
  const velocityRef = useRef({ x: 0, z: 0 });
  const gaitRef = useRef({ phase: 0, amount: 0 });
  // برگشتِ خودکارِ نگاه: از کدام زاویه شروع شد و چند ثانیه راه رفتن از آن گذشته
  const levelRef = useRef({ active: false, from: 0, t: 0 });
  const clockRef = useRef({ now: 0, lastLook: -Infinity });
  const nearbyTargetRef = useRef(null);
  // ارتفاعِ کفِ زیرِ پا (اختلافِ سطح) — نرم دنبال می‌شود تا رد شدن از پله حسِ بالا/پایین رفتن بدهد، نه پرش
  const groundRef = useRef(null);

  useEffect(() => {
    if (!active || posRef.current) return;
    // نقطه‌ی شروع: مرکز بزرگ‌ترین فضای آماده (اگر باشد)، وگرنه مرکز کل ساختمان
    let start = { x: bounds.centerX, z: bounds.centerY };
    if (roomGroupRects.length > 0) {
      const biggest = roomGroupRects.reduce((best, rect) => ((rect.area || 0) > (best.area || 0) ? rect : best), roomGroupRects[0]);
      const c = spaceCenter(biggest);
      start = { x: c.x, z: c.y };
    }
    posRef.current = start;
    yawRef.current = 0;
    pitchRef.current = 0;
  }, [active, bounds, roomGroupRects]);

  useFrame((_, delta) => {
    if (!active || !posRef.current) return;

    // delta بعد از برگشت به تب/قفلِ صفحه ممکن است خیلی بزرگ باشد — محدودش می‌کنیم تا پرش/پرتاب نداشته باشیم
    const dt = Math.min(delta, 0.1);
    const clock = clockRef.current;
    clock.now += dt;

    const look = lookDragRef.current;
    if (look.dx !== 0 || look.dy !== 0) {
      yawRef.current -= look.dx * LOOK_SENSITIVITY;
      pitchRef.current = clamp(pitchRef.current - look.dy * LOOK_SENSITIVITY, -PITCH_LIMIT, PITCH_LIMIT);
      look.dx = 0;
      look.dy = 0;
      clock.lastLook = clock.now;
      levelRef.current.active = false; // کاربر خودش نگاه را کنترل می‌کند — برگشتِ خودکار را متوقف کن
    }

    // بردارهای «جلو» و «راستِ» واقعیِ دوربین، دقیقاً هم‌راستا با همان yaw که پایین‌تر روی خودِ دوربین
    // اعمال می‌شود (قرارداد استاندارد three.js برای دوربینی که رو به -Z نگاه می‌کند)
    const yaw = yawRef.current;
    const forward = { x: -Math.sin(yaw), z: -Math.cos(yaw) };
    const right = { x: Math.cos(yaw), z: -Math.sin(yaw) };

    // سرعتِ هدف از جوی‌استیک، و نزدیک‌شدنِ نرمِ سرعتِ فعلی به آن (شتاب در شروع، ایستِ نرم در توقف)
    const stick = joystickRef.current;
    const hasInput = stick.x !== 0 || stick.y !== 0;
    const targetVx = (forward.x * stick.y + right.x * stick.x) * WALK_MOVE_SPEED_CM_S;
    const targetVz = (forward.z * stick.y + right.z * stick.x) * WALK_MOVE_SPEED_CM_S;
    const vel = velocityRef.current;
    const blend = 1 - Math.exp(-(hasInput ? WALK_ACCEL_RATE : WALK_DECEL_RATE) * dt);
    vel.x += (targetVx - vel.x) * blend;
    vel.z += (targetVz - vel.z) * blend;
    if (!hasInput && Math.hypot(vel.x, vel.z) < 2) {
      vel.x = 0;
      vel.z = 0;
    }

    // حرکتِ واقعی (بعد از برخورد با دیوار) — فاصله‌ی واقعاً طی‌شده مبنای قدم‌هاست، نه ورودیِ جوی‌استیک؛ وقتی
    // کاربر به دیوار چسبیده و جلو را می‌زند، سرش تکان نمی‌خورد
    let traveled = 0;
    if (vel.x !== 0 || vel.z !== 0) {
      const before = posRef.current;
      const after = resolveWalkMove(before, vel.x * dt, vel.z * dt, blockingSegments);
      traveled = Math.hypot(after.x - before.x, after.z - before.z);
      posRef.current = after;
      if (traveled < 1e-6) {
        // به مانع خورده — سرعت را از بین ببر تا بعد از رها کردنِ مانع، یک‌باره پرتاب نشود
        vel.x = 0;
        vel.z = 0;
      }
    }
    const speed = dt > 0 ? traveled / dt : 0;

    // حرکتِ سر: فاز به ازای هر قدم π جلو می‌رود؛ شدتش با سرعت نرم بالا/پایین می‌رود و با توقف محو می‌شود
    const gait = gaitRef.current;
    const speedRatio = clamp(speed / WALK_MOVE_SPEED_CM_S, 0, 1.2);
    gait.amount += (speedRatio - gait.amount) * (1 - Math.exp(-GAIT_BLEND_RATE * dt));
    const prevPhase = gait.phase;
    const nextPhase = prevPhase + (traveled / STEP_LENGTH_CM) * Math.PI;
    // هر بار که فاز از یک مضربِ π رد می‌شود = یک قدم (پا به زمین می‌خورد، همان لحظه‌ی پایین‌ترینِ سر) —
    // صدای قدم، فقط وقتی واقعاً در حالِ راه رفتن است (نه آخرین لغزشِ خیلی کندِ قبل از ایست)
    if (onFootstep && gait.amount > 0.3 && Math.floor(nextPhase / Math.PI) !== Math.floor(prevPhase / Math.PI)) {
      onFootstep(posRef.current);
    }
    gait.phase = nextPhase % (Math.PI * 4);
    const bobY = -HEAD_BOB_CM * gait.amount * Math.cos(2 * gait.phase); // یک چرخه در هر قدم
    const sway = HEAD_SWAY_CM * gait.amount * Math.sin(gait.phase); // یک چرخه در هر دو قدم
    const roll = HEAD_ROLL_RAD * gait.amount * Math.sin(gait.phase);

    // برگشتِ خودکارِ نگاه به روبه‌رو — فقط وقتی در حالِ راه رفتن است و هیچ انگشتی نگاه را کنترل نمی‌کند
    const lookIdle = !look.active && clock.now - clock.lastLook > AUTO_LEVEL_IDLE_S;
    const level = levelRef.current;
    if (lookIdle && speed > AUTO_LEVEL_MIN_SPEED_CM_S && Math.abs(pitchRef.current) > 1e-4) {
      if (!level.active) {
        level.active = true;
        level.from = pitchRef.current;
        level.t = 0;
      }
      level.t += dt;
      const k = smootherstep((level.t - AUTO_LEVEL_DELAY_S) / (AUTO_LEVEL_DURATION_S - AUTO_LEVEL_DELAY_S));
      pitchRef.current = level.from * (1 - k);
    } else {
      level.active = false;
    }

    const groundTarget = floorLevelAt(roomGroupRects, posRef.current.x, posRef.current.z);
    if (groundRef.current == null) groundRef.current = groundTarget;
    groundRef.current += (groundTarget - groundRef.current) * (1 - Math.exp(-GROUND_FOLLOW_RATE * dt));
    const ground = groundRef.current;

    camera.position.set(
      (posRef.current.x + right.x * sway) * M,
      (ground + EYE_HEIGHT_CM + bobY) * M,
      (posRef.current.z + right.z * sway) * M
    );
    camera.rotation.order = 'YXZ';
    camera.rotation.set(pitchRef.current, yawRef.current, roll);

    // پیدا کردنِ دری یا پنجره‌ای که واقعاً وسطِ دیدِ کاربر است — با بردارِ «جلو»یِ کاملِ سه‌بعدیِ
    // دوربین (هم یاو هم پیچ، دقیقاً همان جهتی که وسطِ صفحه‌ی نمایش است)، نه فقط بردارِ افقی؛ وگرنه
    // نگاه‌کردن به بالا/پایین اثری روی تشخیص نمی‌گذاشت. معیار اصلی «مرکزیت» است نه فاصله — از بینِ
    // همه‌ی درها/پنجره‌های داخلِ برد، آن‌که کمترین زاویه با جهتِ نگاه دارد (بیشترین cos) انتخاب
    // می‌شود، نه لزوماً نزدیک‌ترین، چون کاربر باید بتواند از فاصله‌ی معقولی، بدون ایستادن دقیقاً
    // روبه‌رویش، نشانه را ببیند. onNearbyTargetChange فقط وقتی صدا زده می‌شود که نتیجه واقعاً عوض
    // شده باشد (id و kind هر دو)، تا این useFrame باعثِ رندرِ مکررِ درختِ React (که بیرون از Canvas
    // است) نشود
    if (onNearbyTargetChange && interactiveSpots) {
      const yaw = yawRef.current;
      const pitch = pitchRef.current;
      const cosPitch = Math.cos(pitch);
      const forward = {
        x: -cosPitch * Math.sin(yaw),
        y: Math.sin(pitch),
        z: -cosPitch * Math.cos(yaw),
      };
      const eyeY = ground + EYE_HEIGHT_CM;
      const prevTarget = nearbyTargetRef.current;
      const prevKey = prevTarget ? `${prevTarget.kind}:${prevTarget.id}` : null;

      // سقف: اول از همه بررسی می‌شود، چون وقتی سر این‌قدر بالا گرفته شده که سقف دیده می‌شود، عملاً
      // هیچ در/پنجره‌ای هم وسطِ دید نیست که رقابتی با آن معنا داشته باشد. هیسترزیس: اگر همین الان
      // قفلِ سقف بودیم آستانه‌ی رهاکردن (پایین‌تر)، وگرنه آستانه‌ی ورودِ معمول
      const wasOnCeiling = prevTarget && prevTarget.kind === 'ceiling';
      const ceilingPitchThreshold = wasOnCeiling ? CEILING_LOOK_PITCH_RELEASE : CEILING_LOOK_PITCH_ENTER;
      let ceilingRoomId = null;
      if (pitch > ceilingPitchThreshold) {
        const room = roomGroupRects.find((r) => spaceContains(r, posRef.current.x, posRef.current.z));
        if (room) ceilingRoomId = room.id;
      }

      let nextTarget;
      if (ceilingRoomId != null) {
        nextTarget = { id: ceilingRoomId, kind: 'ceiling' };
      } else {
        // برخلافِ قبل (که فقط بالاترین cos را نگه می‌داشت)، این‌جا هم بهترینِ مطلق (topSpot/topCos) و
        // هم مقدارِ cos خودِ هدفِ فعلی (prevCos، اگر هنوز در لیست/برد باشد) را جدا محاسبه می‌کنیم تا
        // پایین‌تر بشود بینِ «رها کردن» و «عوض کردن» با آستانه‌های جدا تصمیم گرفت
        let topSpot = null;
        let topCos = -Infinity;
        let prevCos = -Infinity;
        interactiveSpots.forEach((spot) => {
          const dx = spot.x - posRef.current.x;
          const dy = spot.y - eyeY;
          const dz = spot.z - posRef.current.z;
          const dist = Math.hypot(dx, dy, dz);
          if (dist >= DOOR_INTERACT_RANGE_CM || dist < 0.001) return;
          const cos = (forward.x * dx + forward.y * dy + forward.z * dz) / dist;
          if (cos > topCos) {
            topCos = cos;
            topSpot = spot;
          }
          if (prevKey && `${spot.kind}:${spot.id}` === prevKey) prevCos = cos;
        });

        if (prevTarget && prevTarget.kind !== 'ceiling' && prevCos > DOOR_INTERACT_COS_RELEASE) {
          // هدفِ فعلی هنوز به‌قدرِ کافی وسطِ دید است — فقط اگر نامزدِ دیگری به‌وضوح بهتر باشد عوضش کن
          const topKey = topSpot ? `${topSpot.kind}:${topSpot.id}` : null;
          nextTarget =
            topSpot && topKey !== prevKey && topCos > prevCos + DOOR_INTERACT_SWITCH_MARGIN
              ? { id: topSpot.id, kind: topSpot.kind }
              : prevTarget;
        } else {
          // یا اصلاً هدفی نداشتیم، یا هدفِ قبلی دیگر به‌قدرِ کافی وسطِ دید نیست — آستانه‌ی معمولیِ
          // ورود را اعمال کن
          nextTarget = topSpot && topCos > DOOR_INTERACT_COS_LIMIT ? { id: topSpot.id, kind: topSpot.kind } : null;
        }
      }

      const nextKey = nextTarget ? `${nextTarget.kind}:${nextTarget.id}` : null;
      nearbyTargetRef.current = nextTarget;
      if (prevKey !== nextKey) {
        onNearbyTargetChange(nextTarget);
      }
    }
  });

  return null;
}

function Scene({
  exportRef,
  baked,
  onBakedStatus,
  cloneRef,
  bakeSaveRef,
  localBake,
  onLocalBakeStatus,
  walls,
  doors,
  windows,
  openings,
  roomGroupRects,
  wallThickness,
  doorWidth,
  windowWidth,
  openingWidth,
  viewMode,
  joystickRef,
  lookDragRef,
  posRef,
  yawRef,
  avatarSpot,
  selectedWallId,
  selectedSide,
  onSelectWall,
  openDoorIds,
  selectedDoorId,
  onSelectDoor,
  openWindowIds,
  selectedWindowId,
  onSelectWindow,
  selectedOpeningId,
  onSelectOpening,
  selectedFloorId,
  onSelectFloor,
  onNearbyTargetChange,
  roomLightLevels,
  engineeringOn,
  pitchRef,
  onFootstep,
  live,
}) {
  const wallItems = useMemo(
    () =>
      computeWallItemsByWall(walls, doors, windows, openings, doorWidth, windowWidth, openingWidth, roomGroupRects, wallThickness),
    [walls, doors, windows, openings, doorWidth, windowWidth, openingWidth, roomGroupRects, wallThickness]
  );
  // پایین‌ترین کفِ خانه (اختلافِ سطح) — دیوارها تا همین‌جا پایین می‌آیند تا زیرِ اتاقِ پایین‌تر خالی نماند
  const minLevel = useMemo(() => Math.min(0, ...roomGroupRects.map(roomFloorLevel)), [roomGroupRects]);
  // تکه‌های هر دیوارِ منحنی، به ترتیب
  const curvedGroups = useMemo(() => {
    const map = new Map();
    walls.forEach((w) => {
      if (w.arcParent == null) return;
      if (!map.has(w.arcParent)) map.set(w.arcParent, []);
      map.get(w.arcParent).push(w);
    });
    return [...map.entries()].map(([parent, subs]) => ({
      parent,
      subs: subs.sort((a, b) => Number(String(a.id).split('~')[1]) - Number(String(b.id).split('~')[1])),
    }));
  }, [walls]);
  // زیرِ کلِ خانه (زیرسازیِ کف و سقف) — فقط با تغییرِ دیوارها
  const footprint = useMemo(() => computeSpaces(walls, [], [], [], { footprint: true }), [walls]);
  const levelAt = useCallback((x, z) => floorLevelAt(roomGroupRects, x, z), [roomGroupRects]);
  // بیرونِ خانه در اول‌شخص: فقط آسمانِ واقعی (خیابان/ساختمان‌های ساختگیِ اطراف طبیعی نبودند و حذف شدند).
  // جهتِ خورشید: بالای پلان = شمال
  const showSite = viewMode === 'walk';
  const buildingRef = useRef(null);
  const bounds = useMemo(() => computeSceneBounds(walls), [walls]);
  const blockingSegments = useMemo(
    () => [
      ...computeBlockingSegmentsWorld(walls, doors, windows, openings, doorWidth, windowWidth, openingWidth, openDoorIds),
    ],
    [walls, doors, windows, openings, doorWidth, windowWidth, openingWidth, openDoorIds]
  );
  const doorSpots = useMemo(
    () => computeDoorWorldSpots(walls, doors, windows, openings, doorWidth, windowWidth, openingWidth),
    [walls, doors, windows, openings, doorWidth, windowWidth, openingWidth]
  );
  const windowSpots = useMemo(
    () => computeWindowWorldSpots(walls, doors, windows, openings, doorWidth, windowWidth, openingWidth),
    [walls, doors, windows, openings, doorWidth, windowWidth, openingWidth]
  );
  // نشانه‌ی «دست» در حالت اول‌شخص برای در و پنجره از یک استخرِ مشترک انتخاب می‌شود (هرکدام مرکزیِ
  // صفحه بود، همان یکی نشانه می‌گیرد) — به همین دلیل این دو لیست این‌جا با هم ترکیب می‌شوند
  const interactiveSpots = useMemo(() => [...doorSpots, ...windowSpots], [doorSpots, windowSpots]);

  // سایه‌ی نرمِ زیرِ دیوار/در/پنجره (ContactShadows پایین‌تر) یک‌بار «پخته» و بعد نگه داشته می‌شود، نه
  // این‌که هر فریم دوباره حساب شود (که روی موبایل سنگین است) — فقط با تغییرِ واقعیِ چیدمان (دیوار/
  // در/پنجره/بازشو، هم موقعیت هم استایل) دوباره پخته می‌شود، نه با باز/بسته‌شدنِ موقتیِ در/پنجره در
  // حالتِ اول‌شخص یا راه‌رفتنِ آدمک (که خیلی مکرر عوض می‌شوند و اصلاً لازم نیست رویِ این سایه اثر بگذارند)
  const shadowBakeRef = useRef({ counter: 0, walls, doors, windows, openings });
  if (
    shadowBakeRef.current.walls !== walls ||
    shadowBakeRef.current.doors !== doors ||
    shadowBakeRef.current.windows !== windows ||
    shadowBakeRef.current.openings !== openings
  ) {
    shadowBakeRef.current = { counter: shadowBakeRef.current.counter + 1, walls, doors, windows, openings };
  }
  const shadowBakeKey = shadowBakeRef.current.counter;

  const centerXm = bounds.centerX * M;
  const centerYm = bounds.centerY * M;
  const maxSizeM = Math.max(bounds.sizeX, bounds.sizeY) * M;
  const cameraDistance = Math.max(6, maxSizeM * 1.15);

  // نور به‌صورت یکنواخت از بالا می‌تابد (مثل نور روز پراکنده، نه یک منبع نقطه‌ای/جهت‌دار با زاویه) —
  // hemisphereLight دقیقاً همین مدل را می‌دهد (روشنایی از آسمون از بالا، انعکاس ملایم از کف از پایین)
  // و اصلاً سایه تولید نمی‌کند، پس دیگر نیازی به castShadow/receiveShadow یا دوربین سایه نیست
  return (
    <>
      <hemisphereLight args={['#ffffff', '#d8cfc0', 1.15]} />
      <ambientLight intensity={0.35} />
      {/* نورِ برگشتی (bounce): کفِ گرم و کمی نارنجی، از پایین نور به سقف و زیرِ سطوح می‌دهد — رنگِ گرمِ نور از کف */}
      <directionalLight position={[0, -1, 0]} color="#ffd9a8" intensity={0.22} />
      {/* نورِ خورشید از پنجره: جهت‌دار با سایه‌ی نرم (PCF). سایه‌ها را ShadowFlags روی مش‌ها فعال می‌کند */}
      <directionalLight
        position={[-500, 1100, 700]}
        color="#fff1dc"
        intensity={0.55}
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-bias={-0.0004}
        shadow-normalBias={0.6}
        shadow-radius={5}
      >
        <orthographicCamera attach="shadow-camera" args={[-1400, 1400, 1400, -1400, 1, 4000]} />
      </directionalLight>
      <ShadowFlags />
      {/* شدتِ روشناییِ هر اتاق (تبِ «نور» در پنلِ سقف) — روی همه‌ی متریال‌ها، به تفکیکِ اتاق */}
      <RoomLightingController rooms={roomGroupRects} levels={roomLightLevels} unitScale={M} />

      {/* پس‌زمینه‌ی صحنه یک رنگ خنثیِ ساده است (نه خودِ عکسِ محیط) — preset فقط برای نورپردازی/انعکاس
          واقعی‌تر روی متریال‌ها استفاده می‌شود، نه برای نمایش به‌عنوان «بیرون خانه» */}
      <color attach="background" args={['#c9d2da']} />
      {/* نورِ محیطی از فایلِ خودِ سایت (preset از سایتِ خارجی می‌آمد که در ایران بدونِ VPN باز نمی‌شود و اگر
          نمی‌آمد کلِ صحنه خطا می‌داد). جدا از بقیه‌ی صحنه منتظر می‌ماند و اگر نیامد، صحنه بدونِ آن کار می‌کند */}
      <SafeEnvironment />

      {/* منظره‌ی بیرون (فقط اول‌شخص و وقتی لوکیشن هست): آسمانِ واقعی + مهِ دوردست */}
      {showSite && <SiteSky northPlan={[0, -1]} />}

      {/* زمینه خنثی زیر کل صحنه، تا نور محیطی روی چیزی جز خالی بیفتد — وقتی منظره‌ی بیرون هست لازم نیست
          (و جلوی دیدنِ خیابانِ پایین را می‌گرفت) */}
      {!showSite && (
      <mesh position={[centerXm, (minLevel - 2) * M, centerYm]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[maxSizeM * 6, maxSizeM * 6]} />
        <meshBasicMaterial color="#dfdfd9" />
      </mesh>
      )}

      {/* «رندرِ واقعی»: خروجیِ GLB فقط از همین گروهِ ساختمان (نه آسمان/نورها/دوربین) */}
      <SceneExporter exportRef={exportRef} targetRef={buildingRef} cloneRef={cloneRef} />
      {/* سایه‌ی گوشه‌ها (N8AO) فقط با ?ao=1 فعال است؛ پیش‌فرض خاموش */}
      <SsaoPass enabled={SSAO_ON} />
      {live && <LiveReporter live={live} posRef={posRef} yawRef={yawRef} />}
      {/* «رندرِ واقعی»: نتیجه‌ی سرور جای ساختمانِ قابلِ ویرایش دیده می‌شود (ساختمان فقط مخفی می‌شود، نه حذف —
          برگشتن فوری است و خروجی گرفتن هم همچنان از همین گروه است) */}
      {baked && !baked.local && <BakedModel result={baked} showCeiling={viewMode === 'walk'} onStatus={onBakedStatus} />}
      {baked && baked.local && (
        <LocalBakeView
          key={baked.key}
          cloneRef={cloneRef}
          saveRef={bakeSaveRef}
          showCeiling={viewMode === 'walk'}
          paused={localBake.paused}
          finishSignal={localBake.finishSignal}
          onStatus={onLocalBakeStatus}
        />
      )}
      <BakedLiveMask active={Boolean(baked)} targetRef={buildingRef} />
      <group ref={buildingRef} scale={[M, M, M]}>
        {/* هر فضا ممکن است چند تکه‌ی مستطیلی داشته باشد (مثلاً اتاقِ بزرگی که اتاقِ کوچکی داخلش است) — کفِ
            هر تکه جدا، ولی همه با جنس/انتخابِ همان فضا */}
        {roomGroupRects.map((rect, index) => {
          const pieces = rect.rects && rect.rects.length ? rect.rects : [rect];
          const simple = pieces.length === 1 && !isTrapPiece(pieces[0]);
          return (
            <Floor
              key={rect.id}
              rect={simple ? { ...rect, x1: pieces[0].x1, y1: pieces[0].y1, x2: pieces[0].x2, y2: pieces[0].y2 } : rect}
              pieces={simple ? null : pieces}
              defaultColor={FLOOR_PALETTE[index % FLOOR_PALETTE.length]}
              isSelected={rect.id === selectedFloorId}
              onSelect={() => onSelectFloor && onSelectFloor(rect.id)}
              lightPct={resolveCeilingStyle(rect).lightPct}
              walls={walls}
            />
          );
        })}

        {/* سایه‌ی نرمِ تماسی — به‌جای سایه‌ی واقعیِ سه‌بعدی (که برای هر نور/هر شیء گران است و روی
            موبایل کند می‌شود)، فقط یک بافتِ سایه‌ی محوِ ازپیش‌محاسبه‌شده زیرِ کل صحنه می‌کشد؛ حسِ
            «چسبیدنِ» دیوار/در/پنجره به کف را می‌دهد بدون هزینه‌ی سایه‌ی زنده. frames=1 یعنی فقط یک‌بار
            (با تغییرِ key، یعنی با تغییرِ واقعیِ چیدمان) پخته می‌شود، نه هر فریم */}
        <ContactShadows
          key={shadowBakeKey}
          userData={{ khNoExport: true }}
          position={[bounds.centerX, 0.55, bounds.centerY]}
          scale={Math.max(bounds.sizeX, bounds.sizeY) * 1.4 + 200}
          far={90}
          blur={2.6}
          opacity={0.42}
          resolution={512}
          color="#241d16"
          frames={1}
        />

        {/* دیوارِ منحنی: تکه‌هایش (expandArcs) با هم «یک» سطحِ نرم می‌شوند — نه چند جعبه‌ی پله‌پله */}
        {curvedGroups.map(({ parent, subs }) => (
          <CurvedWall3D
            key={`arc-${parent}`}
            subWalls={subs}
            thickness={wallThickness}
            height={WALL_HEIGHT_CM}
            onSelectWall={onSelectWall}
            highlightSide={subs.some((w) => w.id === selectedWallId) ? selectedSide : null}
            interiorAo={viewMode === 'walk'}
          />
        ))}
        {walls.filter((w) => w.arcParent == null).map((wall) => (
          <Wall3D
            key={wall.id}
            wall={wall}
            allWalls={walls}
            doors={doors}
            windows={windows}
            openings={openings}
            wallThickness={wallThickness}
            doorWidth={doorWidth}
            windowWidth={windowWidth}
            openingWidth={openingWidth}
            onSelectWall={onSelectWall}
            highlightSide={wall.id === selectedWallId ? selectedSide : null}
            openDoorIds={openDoorIds}
            selectedDoorId={selectedDoorId}
            onSelectDoor={onSelectDoor}
            openWindowIds={openWindowIds}
            selectedWindowId={selectedWindowId}
            onSelectWindow={onSelectWindow}
            selectedOpeningId={selectedOpeningId}
            onSelectOpening={onSelectOpening}
            interiorAo={viewMode === 'walk'}
            roomGroupRects={roomGroupRects}
            minLevel={minLevel}
          />
        ))}
        {/* سقف فقط در حالتِ اول‌شخص رندر می‌شود — هم چون خودِ ساختمان از این حالت به آن نیاز دارد
            (وگرنه از داخلِ خانه بالای سر خالی دیده می‌شد)، هم چون تنها همین‌جاست که واقعاً می‌شود
            جنس/رنگش را از نزدیک و با نورِ واقعی دید. در حالتِ دیفالت/اوربیت اصلاً نشان داده نمی‌شود —
            نه فقط برای این‌که جلوِ دیدِ داخلِ خانه (نمایِ عروسکی/dollhouse) را نگیرد، بلکه چون از آن
            فاصله هم عملاً نمی‌شد تغییراتش را دید */}
        {/* در حالتِ پیش‌فرض مخفی است (نه حذف)، تا در خروجیِ «رندرِ واقعی» همیشه باشد (khExportAlways) */}
        <group visible={viewMode === 'walk'} userData={{ khExportAlways: true }}>
          <Ceiling roomGroupRects={roomGroupRects} height={WALL_HEIGHT_CM} walls={walls} />
          {/* زیرسازیِ سقف — اگر جایی بینِ فضاها سقف نگرفت (گوشه‌ی تیزِ مرزِ مورب، تکه‌ی خیلی کوچک)، باز نماند */}
          <FootprintFill pieces={footprint} y={WALL_HEIGHT_CM + 1.5} up={false} color="#f2efe6" />
        </group>
        {/* زیرسازیِ کف — همان، زیرِ همه‌ی کف‌ها */}
        <FootprintFill pieces={footprint} y={minLevel - 1} up color="#d9d2c3" />
        {/* عینکِ مهندسی — خطوطِ اندازه روی دیواری که کاربر نگاهش می‌کند (فقط اول‌شخص) */}
        {/* داخلِ buildingRef است، پس باید «کلید» (khLiveKeep) داشته باشد وگرنه BakedLiveMask هر ۲۰ فریم خطوطش را
            روی لایه‌ی پنهان می‌برد (برچسب‌ها که DOM‌اند می‌مانند، فقط خطوطِ سه‌بعدی ناپدید می‌شوند) */}
        {viewMode === 'walk' && (
          <group userData={{ khLiveKeep: true }}>
            <EngineeringGlasses
              active={engineeringOn}
              walls={walls}
              rooms={roomGroupRects}
              wallItems={wallItems}
              posRef={posRef}
              yawRef={yawRef}
              pitchRef={pitchRef}
              wallThickness={wallThickness}
              wallHeightCm={WALL_HEIGHT_CM}
              levelAt={levelAt}
            />
          </group>
        )}
        {/* بازدیدِ مشترک: آدمکِ بقیه (در هر دو حالت) */}
        {live && <LivePeers live={live} levelAt={levelAt} />}
        {viewMode === 'orbit' && (
          <group userData={{ khNoExport: true, khLiveKeep: true }}>
            <AvatarMarker spot={avatarSpot} groundY={avatarSpot ? levelAt(avatarSpot.x, avatarSpot.z) : 0} />
          </group>
        )}
      </group>

      {viewMode === 'orbit' && (
        <>
          <PerspectiveCamera
            makeDefault
            fov={50}
            position={[centerXm + cameraDistance, cameraDistance * 0.85, centerYm + cameraDistance]}
          />
          <OrbitControls
            target={[centerXm, maxSizeM * 0.15, centerYm]}
            enableDamping
            dampingFactor={0.08}
            minDistance={maxSizeM * 0.3}
            maxDistance={maxSizeM * 3}
            maxPolarAngle={Math.PI / 2.05}
          />
        </>
      )}

      {viewMode === 'walk' && (
        <>
          <PerspectiveCamera makeDefault fov={75} near={0.03} far={1000} />
          <FirstPersonRig
            active
            bounds={bounds}
            roomGroupRects={roomGroupRects}
            blockingSegments={blockingSegments}
            interactiveSpots={interactiveSpots}
            onNearbyTargetChange={onNearbyTargetChange}
            joystickRef={joystickRef}
            lookDragRef={lookDragRef}
            posRef={posRef}
            yawRef={yawRef}
            pitchRef={pitchRef}
            onFootstep={onFootstep}
          />
        </>
      )}
    </>
  );
}

// نمایشِ «رندرِ واقعی»: ساختمانِ زنده (قابلِ ویرایش) پنهان می‌شود، جز لنگه‌های در/پنجره (که باز و بسته می‌شوند)
// و آدمک. پنهان‌کردن با «لایه» است نه visible — چون React خودش visible را روی بعضی گروه‌ها (مثلاً سقف) تنظیم
// می‌کند و آن را برمی‌گرداند؛ لایه را کسی دست نمی‌زند. شیءِ روی لایه‌ی ۳۱ نه دیده می‌شود نه لمس می‌شود
const HIDDEN_LAYER = 31;
function applyLiveMask(root, active) {
  const walk = (obj, keep) => {
    const k = keep || Boolean(obj.userData && (obj.userData.khLeaf || obj.userData.khLiveKeep));
    if (obj.isMesh || obj.isLine || obj.isPoints || obj.isSprite) {
      if (active && !k) obj.layers.set(HIDDEN_LAYER);
      else obj.layers.set(0);
    }
    for (let i = 0; i < obj.children.length; i += 1) walk(obj.children[i], k);
  };
  walk(root, false);
}
function BakedLiveMask({ active, targetRef }) {
  const frameRef = useRef(0);
  useEffect(() => {
    if (targetRef.current) applyLiveMask(targetRef.current, active);
  }, [active, targetRef]);
  // اشیای تازه (مثلاً بعد از باز/بسته شدن یا تغییرِ جنس، React شیءِ نو می‌سازد) — هر چند فریم دوباره
  useFrame(() => {
    if (!active || !targetRef.current) return;
    frameRef.current += 1;
    if (frameRef.current % 20 === 0) applyLiveMask(targetRef.current, true);
  });
  return null;
}

// Environment با خطاگیر: نبودنِ فایلِ نورِ محیطی نباید کلِ سه‌بعدی را از کار بیندازد
class EnvErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.warn('[Create3D] environment map not loaded:', error?.message || error); // eslint-disable-line no-console
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}
function SafeEnvironment() {
  return (
    <EnvErrorBoundary>
      <React.Suspense fallback={null}>
        <Environment files="/hdri/lebombo_1k.hdr" />
      </React.Suspense>
    </EnvErrorBoundary>
  );
}

// سایه‌ها: هر مشِ دیدنی را برای ریختن و گرفتنِ سایه علامت می‌زند (مش‌های نامرئیِ لمسی کنار گذاشته می‌شوند).
// هر ۳۰ فریم یک بار بررسی می‌کند تا مش‌های تازه هم سایه بگیرند
function ShadowFlags() {
  const scene = useThree((s) => s.scene);
  const tick = React.useRef(0);
  useFrame(() => {
    if (tick.current++ % 30) return;
    scene.traverse((o) => {
      if (!o.isMesh) return;
      const m = o.material;
      if (m && (m.visible === false || (m.transparent && m.opacity === 0))) return;
      if (!o.castShadow) o.castShadow = true;
      if (!o.receiveShadow) o.receiveShadow = true;
    });
  });
  return null;
}

const faNum = (n) => Math.round(n).toLocaleString('fa-IR');
function faClock(ms) {
  const total = Math.floor((ms || 0) / 1000);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m.toLocaleString('fa-IR')}:${String(sec).padStart(2, '0').replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d])}`;
}
// پنلِ کوچکِ پیشرفتِ رندر (بالا-چپ): آیکون، عنوان، توضیح، درصد، نوارِ پیشرفت و دکمه‌های آیکونیِ مکث/پایان/بستن
function localHudProps(st, paused) {
  if (!st || st.phase === 'prepare') {
    return { title: 'آماده‌سازی', meta: 'چند لحظه…', progress: null, indeterminate: true, tone: 'working' };
  }
  if (st.phase === 'error') {
    return { title: 'رندر روی این دستگاه ممکن نشد', meta: st.message || '', progress: null, tone: 'error' };
  }
  if (st.phase === 'done') {
    return { title: 'رندرِ واقعی', meta: `${faNum(st.samples)} نمونه در ${faClock(st.elapsedMs)}`, progress: null, tone: 'done' };
  }
  const p = st.target ? st.samples / st.target : 0;
  return {
    title: paused ? 'مکث شده' : 'پختنِ نور',
    meta: `${faNum(st.samples)} از ${faNum(st.target)} نمونه · ${faClock(st.elapsedMs)}`,
    progress: p,
    tone: paused ? 'paused' : 'working',
  };
}

function BakeHud({ icon, title, meta, progress, indeterminate, tone, onPause, paused, onStop, onClose, onSave, saved, saveLabel }) {
  const pct = typeof progress === 'number' ? Math.max(0, Math.min(1, progress)) : null;
  return (
    <div className={`create-3d__hud is-${tone || 'working'}`} onPointerDown={(e) => e.stopPropagation()}>
      <div className="create-3d__hud-row">
        <span className="create-3d__hud-icon">{icon}</span>
        <div className="create-3d__hud-text">
          <strong>{title}</strong>
          {meta && <small>{meta}</small>}
        </div>
        <div className="create-3d__hud-actions">
          {onPause && (
            <button type="button" onClick={onPause} aria-label={paused ? 'ادامه' : 'مکث'} title={paused ? 'ادامه' : 'مکث'}>
              {paused ? <IconPlay size={16} /> : <IconPause size={16} />}
            </button>
          )}
          {onStop && (
            <button type="button" onClick={onStop} aria-label="پایان و حذفِ نویز" title="پایان">
              <IconStop size={15} />
            </button>
          )}
          <button type="button" className="is-close" onClick={onClose} aria-label="بازگشت به طراحی" title="بازگشت به طراحی">
            <IconClose size={16} />
          </button>
        </div>
      </div>
      {(pct !== null || indeterminate) && (
        <div className="create-3d__hud-progress">
          <div className={`create-3d__hud-bar ${indeterminate ? 'is-indeterminate' : ''}`}>
            <i style={pct !== null ? { width: `${pct * 100}%` } : undefined} />
          </div>
          {pct !== null && <span>{faNum(pct * 100)}٪</span>}
        </div>
      )}
      {/* بعد از پایانِ رندر: ذخیره‌ی طرح روی سرور (نه رندر گرفتن) */}
      {(onSave || saved) && (
        <button type="button" className={`create-3d__hud-save ${saved ? 'is-saved' : ''}`} onClick={onSave} disabled={saved}>
          {saved || saveLabel ? <IconCheck size={17} strokeWidth={2.2} /> : <IconUpload size={17} />}
          <span>{saved ? 'روی سرور ذخیره شد' : saveLabel || 'ارسال فایل روی سرور'}</span>
        </button>
      )}
    </div>
  );
}

function Create3D({
  walls,
  doors,
  windows,
  openings,
  roomGroupRects,
  wallThickness,
  doorWidth,
  windowWidth,
  openingWidth,
  onBack,
  onUpdateWall,
  onUpdateWalls,
  onUpdateDoor,
  onUpdateWindow,
  onUpdateOpening,
  onUpdateRoomFloor,
  onUpdateRoomCeiling,
  site,
  // «ورودی»: نقطه‌ی شروعِ بازدید { x, y } (سانتی‌متر، پلان) یا null
  entrance = null,
  // وضعیتِ خامِ ویرایشگرِ دوبعدی (دیوارها، اتاق‌ها، گروه‌ها …) — داخلِ plan.json طرحِ ذخیره‌شده می‌رود
  planSnapshot,
  // آگهی‌ای که این طرح برایش کشیده می‌شود (فرمِ «ثبت فایل جدید») — همراهِ طرح روی سرور ذخیره می‌شود
  listing,
  // «ورود» از ریلز/پروفایل: فقط تماشا و راه رفتن داخلِ طرحِ رندرشده (بدونِ هیچ ابزارِ ویرایش/ذخیره)
  viewerOnly = false,
  initialBaked = null, // { resultUrl, lightmapUrl, meta: { lightmapIntensity } }
  // «پایان طراحی»: بعد از رندر، به‌جای «ارسال روی سرور» — فایل‌ها و فیلم ساخته می‌شوند و به صفحه‌ی آگهی برمی‌گردند
  // (انتشار از همان‌جا). onFinishDesign(prepared)
  onFinishDesign = null,
  // بازدیدِ مشترک (LiveSession از liveClient.jsx) — آدمکِ بقیه، درهای مشترک، صدا
  live = null,
  liveName = '',
}) {
  // با «ورودی»، سه‌بعدی مستقیم از همان نقطه و اول‌شخص شروع می‌شود (دکمه‌ی «پیش‌فرض» = نمای بالا برای ویرایش)
  const [viewMode, setViewMode] = useState(viewerOnly || entrance ? 'walk' : 'orbit');
  // «رندرِ واقعی» در صحنه: { resultUrl, lightmapUrl, meta } یا null (= طراحیِ قابلِ ویرایش)
  const [baked, setBaked] = useState(initialBaked);
  const [bakedStatus, setBakedStatus] = useState(initialBaked ? 'loading' : 'idle'); // loading | ready | error
  const [bakedProgress, setBakedProgress] = useState(0);
  // پختنِ نور روی همین دستگاه
  const cloneRef = useRef(null);
  const [localBake, setLocalBake] = useState({ paused: false, finishSignal: 0 });
  const [localStatus, setLocalStatus] = useState(null);
  const handleLocalBakeStatus = useCallback((st) => setLocalStatus(st), []);
  // در طولِ پختن صفحه خاموش نشود (وقتی صفحه خاموش شود مرورگر کار را متوقف می‌کند). Wake Lock مجوز نمی‌خواهد؛
  // با برگشتن به صفحه دوباره درخواست می‌شود (سیستم‌عامل با پنهان شدنِ صفحه آزادش می‌کند)
  const localBaking = Boolean(baked && baked.local && localStatus && (localStatus.phase === 'baking' || localStatus.phase === 'prepare') && !localBake.paused);
  useEffect(() => {
    if (!localBaking || typeof navigator === 'undefined' || !navigator.wakeLock) return undefined;
    let lock = null;
    let released = false;
    const acquire = async () => {
      try {
        if (document.visibilityState === 'visible') lock = await navigator.wakeLock.request('screen');
        if (released && lock) lock.release().catch(() => {});
      } catch (e) {
        lock = null;
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') acquire();
    };
    acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (lock) lock.release().catch(() => {});
    };
  }, [localBaking]);
  const handleBakedStatus = useCallback((status, p) => {
    setBakedStatus(status);
    if (typeof p === 'number') setBakedProgress(p);
  }, []);
  // تابعِ خروجیِ GLB (SceneExporter داخلِ Canvas پُرش می‌کند) — برای دکمه‌ی «رندرِ واقعی»
  const exportRef = useRef(null);
  // «ارسال فایل روی سرور» (ذخیره‌ی طرحِ رندرشده): فایل‌های رندرِ گوشی (LocalBakeView) و عکسِ صحنه (FrameCapture)
  const bakeSaveRef = useRef(null);
  const captureRef = useRef(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [savedKeys, setSavedKeys] = useState(() => new Set()); // رندرهایی که ذخیره شده‌اند
  const bakedKey = baked ? String(baked.local ? baked.key : baked.id) : null;
  const bakedSaved = Boolean(bakedKey && savedKeys.has(bakedKey));
  // رندر کامل شد (گوشی: پختن تمام شد؛ سرور: بارگذاری شد)
  const renderReady = Boolean(baked && (baked.local ? localStatus && localStatus.phase === 'done' : bakedStatus === 'ready' && baked.id));
  // «عینکِ مهندسی» در حالتِ اول‌شخص (دکمه‌ی بالا-وسط): وقتی روشن است، اندازه‌های دیوارِ روبه‌رو نشان داده می‌شود
  const [engineeringOn, setEngineeringOn] = useState(false);
  // آخرین نقطه‌ای که کاربر در حالت اول‌شخص ایستاده بود، برای نمایش آدمک در حالت دیفالت — فقط وقتی
  // پر می‌شود که کاربر واقعاً از حالت اول‌شخص برگردد (دکمه‌ی «پیش‌فرض» پایین‌تر)
  // «ورودی»: بازدید از همین نقطه شروع می‌شود و آدمک هم همان‌جا می‌ایستد؛ فقط بارِ اول حساب می‌شود
  const entrySpawn = useMemo(() => entranceSpawn(entrance, walls, doors, roomGroupRects), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [avatarSpot, setAvatarSpot] = useState(() => (entrySpawn ? { x: entrySpawn.x, z: entrySpawn.z, yaw: entrySpawn.yaw } : null));
  // دیواری که با تپ روی آن در حالت دیفالت/اوربیت انتخاب شده — پنل تنظیمات دیوار (رنگ/براقیت/قرنیز)
  // فقط وقتی این مقدار پر باشد نمایش داده می‌شود. انتخاب فقط در حالت اوربیت معنا دارد (در حالت
  // اول‌شخص لایه‌ی نگاه‌کردن روی کل صفحه است و اصلاً اجازه نمی‌دهد کلیک به خودِ دیوارها برسد)
  const [selectedWallId, setSelectedWallId] = useState(null);
  // این‌که کدام «رویه»‌ی (سمت) دیوارِ انتخاب‌شده لمس شده — 'a' یا 'b'. تا وقتی این پر نباشد پنل
  // تنظیمات نمایش داده نمی‌شود، چون بدون دانستنِ سمت، معلوم نیست تغییرات باید کجا ذخیره شود
  const [selectedSide, setSelectedSide] = useState(null);
  const selectedWall = walls.find((w) => w.id === selectedWallId) || null;
  const currentSides = selectedWall ? resolveWallSides(selectedWall) : null;
  const currentStyle = selectedSide && currentSides ? currentSides[selectedSide] : null;

  const handleSelectWallSide = (wallId, side) => {
    setSelectedWallId(wallId);
    setSelectedSide(side);
    setSelectedDoorId(null);
          setSelectedOpeningId(null);
    setSelectedWindowId(null);
    setSelectedFloorId(null);
    setSelectedCeilingId(null);
  };

  // پایه‌ی مشترکِ همه‌ی هندلرهای پایین: رویه‌ی فعلیِ همان سمتِ انتخاب‌شده را (یا پیش‌فرض کامل، اگر
  // هنوز چیزی برایش تنظیم نشده) می‌گیرد، تغییرات را رویش اعمال می‌کند و فقط همان یک سمت را در
  // wall.sides جایگزین می‌کند — سمتِ دیگر دست‌نخورده می‌ماند، دقیقاً همان چیزی که رفع‌اش خواسته شده بود
  const patchSide = (changes) => {
    if (selectedWallId == null || !selectedSide || !onUpdateWall) return;
    const baseSides = currentSides || { a: DEFAULT_WALL_SIDE_STYLE, b: DEFAULT_WALL_SIDE_STYLE };
    const baseStyle = baseSides[selectedSide] || DEFAULT_WALL_SIDE_STYLE;
    const nextStyle = { ...baseStyle, ...changes };
    const nextSides = { ...baseSides, [selectedSide]: nextStyle };
    onUpdateWall(selectedWallId, { sides: nextSides });
    editedSideRef.current = { wallId: selectedWallId, side: selectedSide };
  };

  // «همین پوشش برای همه‌ی دیوارهای این اتاق؟» — معمولاً همه‌ی دیوارهای یک اتاق یک‌جورند. بعد از اولین ویرایشِ
  // پوششِ یک دیوار در هر اتاق (وقتی پنلِ دیوار بسته شد یا دیوارِ دیگری انتخاب شد) یک بار پرسیده می‌شود؛
  // «نه» = دیگر برای این اتاق پرسیده نمی‌شود. دکمه‌ی «اعمال روی همه‌ی دیوارهای این اتاق» همیشه در پنل هست
  const editedSideRef = useRef(null);
  const [coverPrompt, setCoverPrompt] = useState(null); // { room, wallId, side }
  const roomOnSide = (wallId, side) => {
    const w = walls.find((x) => x.id === wallId);
    const dir = w && getWallDirection(w);
    if (!dir) return null;
    return wallSideRooms(roomGroupRects, w, dir, dir.len / 2, wallThickness)[side] || null;
  };
  const applyCoverToRoom = (room, wallId, side) => {
    const src = walls.find((x) => x.id === wallId);
    const srcSides = src && resolveWallSides(src);
    const style = srcSides && srcSides[side];
    if (!room || !style) return;
    const patches = {};
    walls.forEach((w) => {
      if (w.id === wallId) return;
      const dir = getWallDirection(w);
      if (!dir) return;
      // دیوارهای خودِ اتاق + هر دیواری که یک سمتش رو به همین اتاق است
      const rooms = wallSideRooms(roomGroupRects, w, dir, dir.len / 2, wallThickness);
      const s2 = rooms.a && rooms.a.id === room.id ? 'a' : rooms.b && rooms.b.id === room.id ? 'b' : null;
      if (!s2) return;
      const base = resolveWallSides(w) || { a: DEFAULT_WALL_SIDE_STYLE, b: DEFAULT_WALL_SIDE_STYLE };
      patches[w.id] = { sides: { ...base, [s2]: { ...style } } };
    });
    if (Object.keys(patches).length) {
      if (onUpdateWalls) onUpdateWalls(patches);
      else Object.entries(patches).forEach(([id, p]) => onUpdateWall(Number.isNaN(Number(id)) ? id : Number(id), p));
    }
  };
  const maybeAskCover = () => {
    const edited = editedSideRef.current;
    editedSideRef.current = null;
    if (!edited) return;
    const room = roomOnSide(edited.wallId, edited.side);
    if (!room || room.coverPrompt) return;
    setCoverPrompt({ room, wallId: edited.wallId, side: edited.side });
  };
  // با عوض شدنِ دیوار/سمتِ انتخاب‌شده (یا بسته شدنِ پنل)، اگر دیوارِ قبلی ویرایش شده بود بپرس
  const lastSelRef = useRef(null);
  useEffect(() => {
    const prev = lastSelRef.current;
    lastSelRef.current = selectedWallId != null ? `${selectedWallId}|${selectedSide}` : null;
    if (prev && prev !== lastSelRef.current) maybeAskCover();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWallId, selectedSide]);

  const handleChangeMain = (mainChanges) => {
    const baseMain = (currentStyle || DEFAULT_WALL_SIDE_STYLE).main || DEFAULT_WALL_SIDE_STYLE.main;
    patchSide({ main: { ...baseMain, ...mainChanges } });
  };

  const handleToggleLower = (enabled) => {
    if (!enabled) {
      patchSide({ lower: null });
      return;
    }
    // پیش‌فرضِ پوششِ پایین: کاشیِ واقعی (کاشیِ کدنویسی‌شده‌ی قبلی حذف شده)
    patchSide({ lower: { type: 'ceramicPbr', color: '#ffffff', heightCm: 120 } });
  };

  const handleChangeLower = (lowerChanges) => {
    const baseLower = (currentStyle || DEFAULT_WALL_SIDE_STYLE).lower || {
      type: 'ceramicPbr',
      color: '#ffffff',
      heightCm: 120,
    };
    patchSide({ lower: { ...baseLower, ...lowerChanges } });
  };

  const handleChangeFinish = (finish) => {
    patchSide({ finish });
  };

  const handleChangeBaseboard = (patch) => {
    const baseBaseboard = (currentStyle || DEFAULT_WALL_SIDE_STYLE).baseboard || {
      enabled: false,
      color: '#cdc6b8',
      height: 9,
    };
    patchSide({ baseboard: { ...baseBaseboard, ...patch } });
  };

  // دری که با تپ روی آن در حالت دیفالت/اوربیت انتخاب شده — پنل تنظیماتِ در (نوع/رنگ/ابعاد) فقط وقتی
  // این مقدار پر باشد نمایش داده می‌شود. انتخابِ در و انتخابِ (سمتِ) دیوار عمداً همیشه یکی‌شان دیگری
  // را پاک می‌کند، چون هر دو پنل هم‌زمان معنا ندارد
  const [selectedDoorId, setSelectedDoorId] = useState(null);
  // بازشوی انتخاب‌شده (پنلِ عرض/ارتفاع/چارچوب) — همان الگوی در
  const [selectedOpeningId, setSelectedOpeningId] = useState(null);
  const selectedOpening = openings.find((o) => o.id === selectedOpeningId) || null;
  const selectedDoor = doors.find((d) => d.id === selectedDoorId) || null;
  const currentDoorStyle = selectedDoor ? resolveDoorStyle(selectedDoor) : null;

  // پنجره‌ای که با تپ روی آن در حالت دیفالت/اوربیت انتخاب شده — دقیقاً همان الگوی selectedDoorId
  // بالا، فقط برای پنجره؛ انتخابِ پنجره هم مثلِ در/دیوار، انتخابِ آن دو تای دیگر را پاک می‌کند
  const [selectedWindowId, setSelectedWindowId] = useState(null);
  const selectedWindow = windows.find((w) => w.id === selectedWindowId) || null;
  const currentWindowStyle = selectedWindow ? resolveWindowStyle(selectedWindow) : null;

  // کفِ اتاقی که با تپ روی آن در حالت دیفالت/اوربیت انتخاب شده — دقیقاً همان الگوی selectedDoorId/
  // selectedWindowId، فقط شناسه‌اش شناسه‌ی «گروهِ اتاق» (roomGroupRects) است، نه در/پنجره؛ کف باز/
  // بسته نمی‌شود، پس فقط رنگ/جنس/الگو دارد، بدون «facing» یا حالتِ بازشدن
  const [selectedFloorId, setSelectedFloorId] = useState(null);
  const selectedFloorIndex = roomGroupRects.findIndex((r) => r.id === selectedFloorId);
  const selectedFloorRect = selectedFloorIndex >= 0 ? roomGroupRects[selectedFloorIndex] : null;
  const currentFloorStyle = selectedFloorRect
    ? resolveFloorStyle(selectedFloorRect, FLOOR_PALETTE[selectedFloorIndex % FLOOR_PALETTE.length])
    : null;

  // سقفِ اتاقی که کاربر در حالِ ادیتِ آن است — برخلافِ selectedFloorId (که با تپِ مستقیم در حالتِ
  // اوربیت پر می‌شود)، سقف فقط از دیدِ اول‌شخص قابل‌دیدن/ویرایش است؛ این state با لمسِ نشانه‌ی
  // «مدادِ» ویرایشِ سقف (پایین‌تر، هر وقت کاربر به سقف نگاه کند ظاهر می‌شود) پر می‌شود، نه با تپِ
  // مستقیم روی خودِ صفحه‌ی سقف
  const [selectedCeilingId, setSelectedCeilingId] = useState(null);
  const selectedCeilingRect = roomGroupRects.find((r) => r.id === selectedCeilingId) || null;
  const currentCeilingStyle = selectedCeilingRect ? resolveCeilingStyle(selectedCeilingRect) : null;

  // درها/پنجره‌هایی که کاربر در همینِ نشستِ فعلیِ حالت اول‌شخص باز کرده — عمداً state موقتی‌اند، نه
  // بخشی از پلانِ ذخیره‌شونده/Undo، چون «باز بودن» یک تصمیمِ لحظه‌ایِ گشت‌وگذار است، نه تصمیمِ طراحی
  // نزدیک‌ترین در به «ورودی» از اول باز است — انگار همین الان دارند وارد خانه می‌شوند
  const entranceDoorIds = useMemo(() => (entrySpawn && entrySpawn.doorId != null ? [entrySpawn.doorId] : []), [entrySpawn]);
  const [openDoorIds, setOpenDoorIds] = useState(() => new Set(entranceDoorIds));
  const [openWindowIds, setOpenWindowIds] = useState(() => new Set());

  // روشناییِ مؤثرِ هر اتاق = تنظیمِ کاربر (سقف ← تبِ «نور») + اثرِ درهای باز/بازشوها (roomLighting.jsx)
  const wallItemCenters = useMemo(
    () => computeWallItemCenters(walls, doors, windows, openings, doorWidth, windowWidth, openingWidth),
    [walls, doors, windows, openings, doorWidth, windowWidth, openingWidth]
  );
  const roomLightLevels = useMemo(
    () =>
      computeRoomLightLevels(
        roomGroupRects,
        wallItemCenters,
        openDoorIds,
        (rect) => resolveCeilingStyle(rect).lightPct,
        openWindowIds
      ),
    [roomGroupRects, wallItemCenters, openDoorIds, openWindowIds]
  );
  // در/پنجره‌ای که همین الان، در حالت اول‌شخص، وسطِ دیدِ کاربر است — {id, kind} یا null. فقط وقتی پر
  // است که نشانه‌ی «دست» روی صفحه نمایش داده می‌شود (پایین‌تر)
  const [nearbyTarget, setNearbyTarget] = useState(null);

  const handleSelectDoor = (doorId) => {
    setSelectedDoorId(doorId);
    setSelectedOpeningId(null);
    setSelectedWindowId(null);
    setSelectedWallId(null);
    setSelectedSide(null);
    setSelectedFloorId(null);
    setSelectedCeilingId(null);
  };

  const handleSelectOpening = (openingId) => {
    setSelectedOpeningId(openingId);
    setSelectedDoorId(null);
    setSelectedWindowId(null);
    setSelectedWallId(null);
    setSelectedSide(null);
    setSelectedFloorId(null);
    setSelectedCeilingId(null);
  };
  const handleChangeOpening = (changes) => {
    if (!selectedOpening || !onUpdateOpening) return;
    onUpdateOpening(selectedOpening.id, { style: { ...(selectedOpening.style || {}), ...changes } });
  };
  // بیشترین عرضِ ممکن: طولِ دیوارِ صاحبِ بازشو منهای کمی حاشیه
  const selectedOpeningMaxWidth = (() => {
    if (!selectedOpening) return 400;
    const w = walls.find((x) => x.id === selectedOpening.wallId);
    const dir = w && getWallDirection(w);
    return dir ? dir.len - 20 : 400;
  })();

  const handleSelectWindow = (windowId) => {
    setSelectedWindowId(windowId);
    setSelectedDoorId(null);
          setSelectedOpeningId(null);
    setSelectedWallId(null);
    setSelectedSide(null);
    setSelectedFloorId(null);
    setSelectedCeilingId(null);
  };

  const handleSelectFloor = (floorId) => {
    setSelectedFloorId(floorId);
    setSelectedDoorId(null);
          setSelectedOpeningId(null);
    setSelectedWindowId(null);
    setSelectedWallId(null);
    setSelectedSide(null);
    setSelectedCeilingId(null);
  };

  const handleSelectCeiling = (ceilingId) => {
    setSelectedCeilingId(ceilingId);
    setSelectedDoorId(null);
          setSelectedOpeningId(null);
    setSelectedWindowId(null);
    setSelectedWallId(null);
    setSelectedSide(null);
    setSelectedFloorId(null);
  };

  // صدای قدم بسته به جنسِ کفِ اتاقی که کاربر در آن قدم برداشته (بیرون از همه‌ی اتاق‌ها = سطحِ سخت)
  const handleFootstep = (pos) => {
    const rect = roomGroupRects.find((r) => spaceContains(r, pos.x, pos.z));
    playFootstep(rect ? resolveFloorStyle(rect).type : 'paint');
  };

  // مرورگرها تا اولین لمس اجازه‌ی پخشِ صدا نمی‌دهند — در حالتِ اول‌شخص هر لمس صدا را «باز» می‌کند (بعد از
  // بارِ اول هزینه‌ای ندارد) و همه‌ی صداها از قبل لود می‌شوند
  useEffect(() => {
    if (viewMode !== 'walk') return undefined;
    const onFirstTouch = () => unlockAudio();
    window.addEventListener('pointerdown', onFirstTouch, true);
    return () => window.removeEventListener('pointerdown', onFirstTouch, true);
  }, [viewMode]);

  const handleToggleDoor = (doorId) => {
    if (doorId == null) return;
    const doorData = doors.find((d) => d.id === doorId);
    playDoorSound(resolveDoorStyle(doorData), !openDoorIds.has(doorId));
    if (live) live.sendDoor(doorId, !openDoorIds.has(doorId));
    setOpenDoorIds((prev) => {
      const next = new Set(prev);
      if (next.has(doorId)) next.delete(doorId);
      else next.add(doorId);
      return next;
    });
  };

  // بازدیدِ مشترک: درهایی که بقیه باز/بسته می‌کنند، و پیامِ «فلانی وارد شد/رفت»
  useEffect(() => {
    if (!live) return undefined;
    const offDoor = live.onDoor((ev) => {
      if (ev.all) {
        setOpenDoorIds(new Set([...doors.filter((d) => ev.all.has(String(d.id))).map((d) => d.id), ...entranceDoorIds]));
        return;
      }
      const door = doors.find((d) => String(d.id) === String(ev.id));
      if (!door) return;
      playDoorSound(resolveDoorStyle(door), ev.open);
      setOpenDoorIds((prev) => {
        const next = new Set(prev);
        if (ev.open) next.add(door.id);
        else next.delete(door.id);
        return next;
      });
    });
    const offEvent = live.onEvent((ev) => {
      if (ev.type === 'join') showToast(`${ev.name} وارد خانه شد`);
      else if (ev.type === 'leave') showToast(`${ev.name} از خانه رفت`);
      else if (ev.type === 'full') showToast(`این بازدید پر است (حداکثر ${Number(ev.max || 4).toLocaleString('fa-IR')} نفر)`);
      else if (ev.type === 'mic-denied') showToast('میکروفون اجازه داده نشد — صدای بقیه را می‌شنوید');
      else if (ev.type === 'reconnect') showToast('اتصال دوباره برقرار شد');
    });
    return () => {
      offDoor();
      offEvent();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, doors, entranceDoorIds]);

  const handleToggleWindow = (windowId) => {
    if (windowId == null) return;
    const winData = windows.find((w) => w.id === windowId);
    playWindowSound(resolveWindowStyle(winData), !openWindowIds.has(windowId));
    setOpenWindowIds((prev) => {
      const next = new Set(prev);
      if (next.has(windowId)) next.delete(windowId);
      else next.add(windowId);
      return next;
    });
  };

  const patchDoorStyle = (changes) => {
    if (selectedDoorId == null || !onUpdateDoor) return;
    const base = currentDoorStyle || DEFAULT_DOOR_STYLE;
    onUpdateDoor(selectedDoorId, { style: { ...base, ...changes } });
  };

  const handleChangeDoorType = (type) => patchDoorStyle({ type });
  const handleChangeDoorColor = (color) => patchDoorStyle({ color });
  const handleChangeDoorAccent = (accent) => patchDoorStyle({ accent });
  const handleChangeDoorSize = (patch) => patchDoorStyle(patch);
  // وقتی چوبِ واقعی انتخاب می‌شود، رنگِ ضرب‌شونده سفید می‌شود تا خودِ عکسِ چوب با رنگِ طبیعی‌اش دیده شود
  // (رنگِ قهوه‌ایِ پیش‌فرضِ در، روی عکسِ چوب ضرب می‌شد و آن را خیلی تیره می‌کرد). برای درهای شیشه‌ای/کشویی
  // قاب از accent ساخته می‌شود، پس همان سفید می‌شود. هر دو در یک patch — دو patchِ پشتِ‌سرِهم یکدیگر را
  // بازنویسی می‌کردند
  const handleChangeDoorMaterial = (material) => {
    if (material !== 'woodPine' && material !== 'woodDark') return patchDoorStyle({ material });
    const t = currentDoorStyle?.type;
    const framedByAccent = t === 'glassModern' || t === 'sliding';
    return patchDoorStyle(framedByAccent ? { material, accent: '#ffffff' } : { material, color: '#ffffff' });
  };

  const patchWindowStyle = (changes) => {
    if (selectedWindowId == null || !onUpdateWindow) return;
    const base = currentWindowStyle || DEFAULT_WINDOW_STYLE;
    onUpdateWindow(selectedWindowId, { style: { ...base, ...changes } });
  };

  const handleChangeWindowType = (type) => patchWindowStyle({ type });
  const handleChangeWindowColor = (color) => patchWindowStyle({ color });
  const handleChangeWindowAccent = (accent) => patchWindowStyle({ accent });
  const handleChangeWindowSize = (patch) => patchWindowStyle(patch);
  const handleChangeWindowFacing = (facing) => patchWindowStyle({ facing });
  // همان منطقِ در: با انتخابِ چوب، رنگِ قاب سفید می‌شود تا رنگِ طبیعیِ عکسِ چوب دیده شود
  const handleChangeWindowMaterial = (material) =>
    patchWindowStyle(
      material === 'woodPine' || material === 'woodDark' ? { material, color: '#ffffff' } : { material }
    );

  // دقیقاً همان الگوی patchDoorStyle/patchWindowStyle، فقط این‌جا به‌جای style روی خودِ door/window،
  // روی floorStyleِ همان «گروهِ اتاق» (roomGroups، در createPlan.jsx) اعمال می‌شود — عمداً بخشی از
  // تاریخچه/Undo نیست، دقیقاً مثلِ بقیه‌ی خصوصیاتِ خودِ roomGroups (برچسب و غیره) که از قبل هم
  // بیرون از سیستمِ Undo مدیریت می‌شدند
  const patchFloorStyle = (changes) => {
    if (selectedFloorId == null || !onUpdateRoomFloor) return;
    const base = currentFloorStyle || DEFAULT_FLOOR_STYLE;
    onUpdateRoomFloor(selectedFloorId, { floorStyle: { ...base, ...changes } });
  };

  // با انتخابِ یک جنسِ واقعی (عکس‌دار)، رنگ سفید می‌شود تا رنگِ طبیعیِ خودِ عکس دیده شود — وگرنه رنگِ
  // پیش‌فرضِ اتاق (از پالت) روی عکس ضرب می‌شد و مثلاً پارکت را صورتی/آبی نشان می‌داد
  const handleChangeFloorType = (type) =>
    patchFloorStyle(type === 'paint' ? { type } : { type, color: '#ffffff' });
  const handleChangeFloorColor = (color) => patchFloorStyle({ color });
  const handleChangeFloorAccent = (accent) => patchFloorStyle({ accent });
  const handleChangeFloorSize = (patch) => patchFloorStyle(patch);

  // دقیقاً همان الگوی patchFloorStyle، فقط روی ceilingStyleِ همان «گروهِ اتاق» — این هم عمداً بخشی
  // از تاریخچه/Undo نیست، دقیقاً مثلِ floorStyle
  const patchCeilingStyle = (changes) => {
    if (selectedCeilingId == null || !onUpdateRoomCeiling) return;
    const base = currentCeilingStyle || DEFAULT_CEILING_STYLE;
    onUpdateRoomCeiling(selectedCeilingId, { ceilingStyle: { ...base, ...changes } });
  };

  // «چندسطحی + نورِ مخفی» با رنگِ فرعیِ پیش‌فرض (خاکستری) نورِ کدری می‌داد — اگر کاربر هنوز رنگِ فرعی را
  // عوض نکرده، رنگِ رایجِ LED (سفیدِ گرم) گذاشته می‌شود
  const handleChangeCeilingType = (type) =>
    patchCeilingStyle(
      type === 'multiCove' && (currentCeilingStyle?.accent || DEFAULT_CEILING_STYLE.accent) === DEFAULT_CEILING_STYLE.accent
        ? { type, accent: '#ffdf9b' }
        : { type }
    );
  const handleChangeCeilingLight = (lightPct) => patchCeilingStyle({ lightPct });
  // «جنس»ِ سقف جدا از «مدل»ش است (نگاه کنید به Ceiling3D.jsx). با انتخابِ چوب، رنگِ اصلی سفید می‌شود تا
  // رنگِ طبیعیِ خودِ عکسِ چوب دیده شود (همان منطقِ در/پنجره)
  const handleChangeCeilingMaterial = (material) =>
    patchCeilingStyle(
      material === 'woodPine' || material === 'woodDark' ? { material, color: '#ffffff' } : { material }
    );
  const handleChangeCeilingColor = (color) => patchCeilingStyle({ color });
  const handleChangeCeilingAccent = (accent) => patchCeilingStyle({ accent });
  const handleChangeCeilingSize = (patch) => patchCeilingStyle(patch);

  const joystickRef = useRef({ x: 0, y: 0 });
  // active: آیا الان انگشتی روی لایه‌ی نگاه‌کردن هست (برای «برگشتِ خودکارِ نگاه» در FirstPersonRig)
  const lookDragRef = useRef({ dx: 0, dy: 0, active: false });
  const lookPointerId = useRef(null);
  const lastLookPos = useRef({ x: 0, y: 0 });
  // موقعیت/چرخشِ لحظه‌ایِ کاربر در حالت اول‌شخص — عمداً این‌جا (بیرون از FirstPersonRig) نگه‌داشته
  // می‌شوند تا وقتی کاربر دکمه‌ی بازگشت به دیفالت را می‌زند، بشود همان لحظه مقدارشان را خواند و در
  // avatarSpot کپی کرد؛ چون این ref‌ها دیگر با خودِ FirstPersonRig از بین نمی‌روند، اگر کاربر دوباره
  // وارد حالت اول‌شخص شود دقیقاً از همان نقطه‌ی قبلی ادامه می‌دهد، نه از وسط بزرگ‌ترین اتاق
  const walkPosRef = useRef(entrySpawn ? { x: entrySpawn.x, z: entrySpawn.z } : null);
  const walkYawRef = useRef(entrySpawn ? entrySpawn.yaw : 0);

  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = useCallback((text) => {
    clearTimeout(toastTimerRef.current);
    setToast({ text, key: Date.now() });
    toastTimerRef.current = setTimeout(() => setToast(null), 2200);
  }, []);
  useEffect(() => () => clearTimeout(toastTimerRef.current), []);
  // رندر تمام شد → پیام (تا کاربر بداند و «پایان طراحی» را بزند)
  useEffect(() => {
    if (onFinishDesign && renderReady) showToast('رندر تمام شد — «پایان طراحی» را روی کادرِ رندر بزنید');
  }, [renderReady, onFinishDesign, showToast]);

  const walkPitchRef = useRef(0);

  const handleGoToDefault = () => {
    if (viewMode === 'walk' && walkPosRef.current) {
      setAvatarSpot({ x: walkPosRef.current.x, z: walkPosRef.current.z, yaw: walkYawRef.current });
    }
    setViewMode('orbit');
    // بیرون از حالت اول‌شخص، «نگاه‌کردن به در/پنجره» دیگر معنا ندارد
    setNearbyTarget(null);
    // اگر پنلِ ادیتِ سقف از دیدِ اول‌شخص باز مانده بود، همین‌جا می‌بندیمش — وگرنه با برگشتن به حالتِ
    // اول‌شخص در آینده، بدونِ لمسِ دوبارهٔ نشانه‌ی «ویرایشِ سقف»، پنل خودبه‌خود دوباره باز می‌شد
    setSelectedCeilingId(null);
  };

  // درگ لمسی روی هر نقطه از صفحه (به‌جز جوی‌استیک و دکمه‌ها) برای نگاه‌کردن در حالت اول‌شخص —
  // فقط تفاضل حرکت انگشت را در یک ref انباشته می‌کند تا FirstPersonRig هر فریم مصرفش کند
  const handleLookPointerDown = (e) => {
    lookPointerId.current = e.pointerId;
    lookDragRef.current.active = true;
    lastLookPos.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleLookPointerMove = (e) => {
    if (lookPointerId.current !== e.pointerId) return;
    const dx = e.clientX - lastLookPos.current.x;
    const dy = e.clientY - lastLookPos.current.y;
    lastLookPos.current = { x: e.clientX, y: e.clientY };
    lookDragRef.current.dx += dx;
    lookDragRef.current.dy += dy;
  };

  const handleLookPointerEnd = (e) => {
    if (lookPointerId.current !== e.pointerId) return;
    lookPointerId.current = null;
    lookDragRef.current.active = false;
  };

  // فایل‌های طرح برای «ارسال فایل روی سرور»: نقشه‌ی خام + عکس، و برای رندرِ گوشی مدل و نقشه‌ی نور (فشرده).
  // رندرِ سرور مدل و نقشه‌ی نورش از قبل روی سرور است — فقط شماره‌ی کار فرستاده می‌شود
  // ui: { onFilmCanvas(canvas), onFilmProgress(0..1) } — پیش‌نمایشِ زنده‌ی ضبطِ فیلم در پنل
  const prepareDesignFiles = async (setStep, ui = {}) => {
    setStep('plan');
    const plan = {
      version: 1,
      app: 'khoonero',
      savedAt: new Date().toISOString(),
      editor: planSnapshot || null,
      scene: { walls, doors, windows, openings, roomGroupRects, entrance: entrance || null, site: site || null, wallThickness, doorWidth, windowWidth, openingWidth },
    };
    const files = { 'plan.json': new Blob([JSON.stringify(plan)], { type: 'application/json' }) };
    // آگهی: اطلاعات (بدونِ آدرسِ موقتِ عکس‌ها) + خودِ عکس‌ها، کوچک‌شده و فشرده
    let listingMeta = null;
    if (listing) {
      const { photos, ...rest } = listing;
      listingMeta = rest;
      const list = (photos || []).filter((ph) => ph && ph.url).slice(0, 8);
      if (list.length) setStep('photos');
      for (let i = 0; i < list.length; i += 1) {
        try {
          files[`photo-${i + 1}.jpg`] = await compressPhoto(list[i].url);
        } catch (e) {
          /* عکسی که دیگر در دسترس نیست — بقیه ذخیره شوند */
        }
      }
      // شماره‌گذاریِ پشتِ سرِ هم (اگر عکسی جا افتاد)
      const names = Object.keys(files).filter((n) => n.startsWith('photo-'));
      names.forEach((n, i) => {
        const blob = files[n];
        delete files[n];
        files[`photo-${i + 1}.jpg`] = blob;
      });
    }
    setStep('thumb');
    if (!captureRef.current) throw new Error('no-capture');
    files['thumb.jpg'] = await captureRef.current();
    // فیلمِ کوتاهِ طرح (پروفایل/اکسپلور) — اگر نشد، طرح بدونِ فیلم ذخیره می‌شود. ui.angles: نماهایی که کاربر تیک زده
    let filmSegments = null;
    const addFilm = async (source) => {
      setStep('film');
      try {
        const film = await recordDesignFilm({
          ...source,
          plan,
          angles: ui.angles,
          onCanvas: ui.onFilmCanvas,
          onProgress: ui.onFilmProgress,
        });
        if (film) {
          files[`film.${film.ext}`] = film.blob;
          filmSegments = film.segments || null;
        }
      } catch (e) {
        console.warn('[film]', e); // eslint-disable-line no-console
      }
    };
    if (!baked.local) {
      await addFilm({
        modelUrl: baked.resultUrl,
        lightmapUrl: baked.lightmapUrl,
        intensity: Number(baked.meta && baked.meta.lightmapIntensity) || 4,
      });
      return { meta: { renderSource: 'server', jobId: baked.id, listing: listingMeta }, files, filmSegments };
    }
    if (!bakeSaveRef.current) throw new Error('not-finished');
    const r = await bakeSaveRef.current(setStep);
    setStep('compress');
    const model = await compressBlob(r.glb, 'model/gltf-binary');
    files[model.gzip ? 'model.glb.gz' : 'model.glb'] = model.blob;
    files['lightmap.jpg'] = r.lightmap;
    await addFilm({ glb: r.glb, lightmap: r.lightmap, intensity: r.intensity });
    return {
      meta: { renderSource: 'device', samples: r.samples, lightmapSize: r.size, lightmapIntensity: r.intensity, listing: listingMeta },
      files,
      filmSegments,
    };
  };

  return (
    <div className={`create-3d ${viewerOnly ? 'is-viewer' : ''}`}>
      <Canvas
        shadows={THREE.PCFShadowMap}
        gl={{ antialias: true, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.05 }}
        // تپ روی جای خالی (هیچ دیواری زیرش نبود) یعنی کاربر می‌خواهد از انتخاب فعلی خارج شود
        onPointerMissed={() => {
          setSelectedWallId(null);
          setSelectedSide(null);
          setSelectedDoorId(null);
          setSelectedOpeningId(null);
          setSelectedWindowId(null);
          setSelectedFloorId(null);
          setSelectedCeilingId(null);
        }}
      >
        <React.Suspense fallback={null}>
          <Scene
            exportRef={exportRef}
            baked={baked}
            onBakedStatus={handleBakedStatus}
            cloneRef={cloneRef}
            bakeSaveRef={bakeSaveRef}
            localBake={localBake}
            onLocalBakeStatus={handleLocalBakeStatus}
            walls={walls}
            doors={doors}
            windows={windows}
            openings={openings}
            roomGroupRects={roomGroupRects}
            wallThickness={wallThickness}
            doorWidth={doorWidth}
            windowWidth={windowWidth}
            openingWidth={openingWidth}
            viewMode={viewMode}
            joystickRef={joystickRef}
            lookDragRef={lookDragRef}
            posRef={walkPosRef}
            yawRef={walkYawRef}
            avatarSpot={avatarSpot}
            selectedWallId={selectedWallId}
            selectedSide={selectedSide}
            onSelectWall={baked ? undefined : handleSelectWallSide}
            openDoorIds={openDoorIds}
            selectedDoorId={selectedDoorId}
            onSelectDoor={baked ? undefined : handleSelectDoor}
            openWindowIds={openWindowIds}
            selectedWindowId={selectedWindowId}
            onSelectWindow={baked ? undefined : handleSelectWindow}
            selectedOpeningId={selectedOpeningId}
            onSelectOpening={baked ? undefined : handleSelectOpening}
            selectedFloorId={selectedFloorId}
            onSelectFloor={baked ? undefined : handleSelectFloor}
            onNearbyTargetChange={setNearbyTarget}
            roomLightLevels={roomLightLevels}
            engineeringOn={engineeringOn}
            pitchRef={walkPitchRef}
            onFootstep={handleFootstep}
            live={live}
          />
        </React.Suspense>
        <FrameCapture captureRef={captureRef} />
      </Canvas>

      {viewMode === 'walk' && (
        <div
          className="create-3d__look-layer"
          onPointerDown={handleLookPointerDown}
          onPointerMove={handleLookPointerMove}
          onPointerUp={handleLookPointerEnd}
          onPointerCancel={handleLookPointerEnd}
        />
      )}

      {/* وقتی کاربر در حالِ ادیتِ سقف است (پنلِ CeilingStyleBox از دیدِ اول‌شخص باز است)، جوی‌استیکِ
          حرکت را موقتاً مخفی می‌کنیم — منطقی نیست هم‌زمان راه برود هم رنگ/جنسِ سقف را تنظیم کند، و
          این‌طوری صفحه هم برای پنلِ تنظیمات شلوغ نمی‌شود */}
      {viewMode === 'walk' && !selectedCeilingRect && (
        <Joystick
          onChange={(vec) => {
            joystickRef.current = vec;
          }}
          onRelease={() => {
            joystickRef.current = { x: 0, y: 0 };
          }}
        />
      )}

      {/* نشانه‌ی «دست» در حالت اول‌شخص — فقط وقتی کاربر واقعاً رو به یک در/پنجره ایستاده و نگاهش
          می‌کند، یا سرش را به‌اندازه‌ی کافی به‌سمتِ سقف بالا گرفته، ظاهر می‌شود (nearbyTarget، هر فریم
          در FirstPersonRig محاسبه می‌شود). لمسِ این دکمه برای در/پنجره باز/بسته‌شان می‌کند، و برای
          سقف پنلِ تنظیماتش (CeilingStyleBox) را همین‌جا از دیدِ اول‌شخص باز می‌کند — چون این دکمه از
          نظرِ چیدمانِ DOM جدا از لایه‌ی تمام‌صفحه‌ی «نگاه‌کردن» است (create-3d__look-layer)، لمسش با
          آن تداخل نمی‌کند و فقط خودش را می‌گیرد. اگر پنلِ سقف از قبل باز باشد، دیگر این نشانه را
          دوباره نشان نمی‌دهیم (حتی اگر هنوز نگاه بالا باشد) تا اضافی/مزاحم نباشد */}
      {viewMode === 'walk' && nearbyTarget != null && !(baked && nearbyTarget.kind === 'ceiling') && !(nearbyTarget.kind === 'ceiling' && selectedCeilingId != null) && (
        <button
          type="button"
          className="create-3d__door-hint"
          onPointerDown={(e) => {
            e.stopPropagation();
            if (nearbyTarget.kind === 'ceiling') handleSelectCeiling(nearbyTarget.id);
            else if (nearbyTarget.kind === 'window') handleToggleWindow(nearbyTarget.id);
            else handleToggleDoor(nearbyTarget.id);
          }}
          aria-label={
            nearbyTarget.kind === 'ceiling'
              ? 'ویرایشِ سقف'
              : (nearbyTarget.kind === 'window' ? openWindowIds : openDoorIds).has(nearbyTarget.id)
              ? nearbyTarget.kind === 'window'
                ? 'بستن پنجره'
                : 'بستن در'
              : nearbyTarget.kind === 'window'
              ? 'باز کردن پنجره'
              : 'باز کردن در'
          }
        >
          {nearbyTarget.kind === 'ceiling' ? (
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none">
              <path
                d="M4 20.2 8.3 19l10.1-10.1a2.1 2.1 0 0 0-3-3L5.3 15.9 4 20.2Z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M14.2 7.1 17.2 10.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
              <path
                d="M7 2.5c-1 0-1.8.8-1.8 1.8v15.4c0 1 .8 1.8 1.8 1.8h1.4M7 2.5h9.6c1 0 1.8.8 1.8 1.8v15.4c0 1-.8 1.8-1.8 1.8H8.4M13.6 12v.01"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      )}

      {/* پنل ادیت دیوار — فقط در حالت دیفالت/اوربیت معنا دارد و فقط وقتی یک «رویه»‌ی دیوار (نه کل
          دیوار) واقعاً لمس شده؛ بدون این، مشخص نیست تغییرات باید روی کدام سمت ذخیره شوند */}
      {viewMode === 'orbit' && selectedWall && selectedSide && roomOnSide(selectedWallId, selectedSide) && (
        <button
          type="button"
          className="create-3d__cover-all"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => {
            const room = roomOnSide(selectedWallId, selectedSide);
            applyCoverToRoom(room, selectedWallId, selectedSide);
            if (room && onUpdateRoomFloor) onUpdateRoomFloor(room.id, { coverPrompt: 'applied' });
            editedSideRef.current = null;
            showToast(`پوشش روی همه‌ی دیوارهای ${room?.label || 'این اتاق'} اعمال شد`);
          }}
        >
          اعمال روی همه‌ی دیوارهای {roomOnSide(selectedWallId, selectedSide)?.label || 'این اتاق'}
        </button>
      )}

      {coverPrompt && (
        <div className="create-3d__dialog" onPointerDown={(e) => e.stopPropagation()}>
          <div className="create-3d__dialog-card">
            <strong>همین پوشش برای بقیه‌ی دیوارها؟</strong>
            <p>
              می‌خواهید همه‌ی دیوارهای {coverPrompt.room.label || 'این اتاق'} هم همین پوشش را بگیرند؟ (اگر «نه» بزنید،
              دیگر درباره‌ی این اتاق پرسیده نمی‌شود.)
            </p>
            <div className="create-3d__dialog-actions">
              <button
                type="button"
                className="is-primary"
                onClick={() => {
                  applyCoverToRoom(coverPrompt.room, coverPrompt.wallId, coverPrompt.side);
                  if (onUpdateRoomFloor) onUpdateRoomFloor(coverPrompt.room.id, { coverPrompt: 'applied' });
                  setCoverPrompt(null);
                }}
              >
                بله، همه‌ی دیوارها
              </button>
              <button
                type="button"
                onClick={() => {
                  if (onUpdateRoomFloor) onUpdateRoomFloor(coverPrompt.room.id, { coverPrompt: 'dismissed' });
                  setCoverPrompt(null);
                }}
              >
                نه
              </button>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'orbit' && selectedWall && selectedSide && (
        <WallStyleBox
          sideStyle={currentStyle}
          wallHeight={WALL_HEIGHT_CM}
          onChangeMain={handleChangeMain}
          onToggleLower={handleToggleLower}
          onChangeLower={handleChangeLower}
          onChangeFinish={handleChangeFinish}
          onChangeBaseboard={handleChangeBaseboard}
          onClose={() => {
            setSelectedWallId(null);
            setSelectedSide(null);
          }}
        />
      )}

      {/* پنل ادیت در — فقط در حالت دیفالت/اوربیت، فقط وقتی یک در واقعاً لمس شده */}
      {viewMode === 'orbit' && selectedOpening && (
        <OpeningStyleBox
          style={selectedOpening.style}
          defaultWidthCm={openingWidth}
          defaultHeightCm={OPENING_HEIGHT_CM}
          wallHeightCm={WALL_HEIGHT_CM}
          maxWidthCm={selectedOpeningMaxWidth}
          onChange={handleChangeOpening}
          onClose={() => setSelectedOpeningId(null)}
        />
      )}

      {viewMode === 'orbit' && selectedDoor && (
        <DoorStyleBox
          style={currentDoorStyle}
          defaultWidthCm={doorWidth}
          wallHeightCm={WALL_HEIGHT_CM}
          onChangeType={handleChangeDoorType}
          onChangeColor={handleChangeDoorColor}
          onChangeAccent={handleChangeDoorAccent}
          onChangeMaterial={handleChangeDoorMaterial}
          onChangeSize={handleChangeDoorSize}
          facing={selectedDoor?.facing}
          hinge={selectedDoor?.hinge}
          onChangeFacing={(facing) => selectedDoorId != null && onUpdateDoor && onUpdateDoor(selectedDoorId, { facing })}
          onFlipHinge={() =>
            selectedDoorId != null &&
            onUpdateDoor &&
            onUpdateDoor(selectedDoorId, { hinge: selectedDoor && selectedDoor.hinge === 'end' ? 'start' : 'end' })
          }
          onClose={() => setSelectedDoorId(null)}
        />
      )}

      {/* پنل ادیت پنجره — دقیقاً همان الگوی پنل ادیت در، فقط وقتی یک پنجره واقعاً لمس شده */}
      {viewMode === 'orbit' && selectedWindow && (
        <WindowStyleBox
          style={currentWindowStyle}
          defaultWidthCm={windowWidth}
          defaultHeightCm={WINDOW_HEADER_CM - WINDOW_SILL_CM}
          defaultSillCm={WINDOW_SILL_CM}
          wallHeightCm={WALL_HEIGHT_CM}
          onChangeType={handleChangeWindowType}
          onChangeColor={handleChangeWindowColor}
          onChangeAccent={handleChangeWindowAccent}
          onChangeSize={handleChangeWindowSize}
          onChangeFacing={handleChangeWindowFacing}
          onChangeMaterial={handleChangeWindowMaterial}
          onClose={() => setSelectedWindowId(null)}
        />
      )}

      {/* پنل ادیت کف — دقیقاً همان الگوی پنل‌های در/پنجره، فقط وقتی کفِ یک اتاق واقعاً لمس شده؛ کف
          نه «باز/بسته» دارد نه «جهت»، فقط نوعِ جنس/رنگ/اندازه‌ی الگو */}
      {viewMode === 'orbit' && selectedFloorRect && (
        <FloorStyleBox
          style={currentFloorStyle}
          onChangeType={handleChangeFloorType}
          onChangeColor={handleChangeFloorColor}
          onChangeAccent={handleChangeFloorAccent}
          onChangeSize={handleChangeFloorSize}
          onClose={() => setSelectedFloorId(null)}
        />
      )}

      {/* پنل ادیت سقف — فقط از دیدِ اول‌شخص باز می‌شود (با لمسِ نشانه‌ی «ویرایشِ سقف»، بالاتر)، چون
          فقط همان‌جا می‌شود واقعاً از نزدیک و با نورپردازیِ واقعی اثرِ هر تغییر (نوع/رنگ/اندازه‌ی
          الگو) را دید */}
      {viewMode === 'walk' && selectedCeilingRect && (
        <CeilingStyleBox
          style={currentCeilingStyle}
          onChangeType={handleChangeCeilingType}
          onChangeMaterial={handleChangeCeilingMaterial}
          onChangeColor={handleChangeCeilingColor}
          onChangeAccent={handleChangeCeilingAccent}
          onChangeSize={handleChangeCeilingSize}
          onChangeLight={handleChangeCeilingLight}
          effectiveLight={roomLightLevels.get(selectedCeilingRect.id)}
          onClose={() => setSelectedCeilingId(null)}
        />
      )}

      {/* عینکِ مهندسی در هر بازدیدِ سه‌بعدی (طراحی، رندر، ورود از اکسپلور/ریلز/لینک، بازدیدِ مشترک) */}
      {viewMode === 'walk' && (
        <button
          type="button"
          className={`create-3d__glasses ${engineeringOn ? 'is-active' : ''}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setEngineeringOn((v) => !v)}
          aria-pressed={engineeringOn}
          aria-label={engineeringOn ? 'خاموش کردنِ عینکِ مهندسی' : 'روشن کردنِ عینکِ مهندسی'}
        >
          <IconGlasses size={22} />
        </button>
      )}

      <div className="create-3d__mode-switch">
        <button type="button" className={viewMode === 'orbit' ? 'is-active' : ''} onClick={handleGoToDefault}>
          <IconCube size={17} />
          <span>پیش‌فرض</span>
        </button>
        <button
          type="button"
          className={viewMode === 'walk' ? 'is-active' : ''}
          onClick={() => {
            setSelectedWallId(null);
            setSelectedSide(null);
            setSelectedDoorId(null);
          setSelectedOpeningId(null);
            setSelectedWindowId(null);
            setSelectedFloorId(null);
            setSelectedCeilingId(null);
            unlockAudio();
            setViewMode('walk');
          }}
        >
          <IconWalk size={17} />
          <span>اول‌شخص</span>
        </button>
      </div>

      {toast && (
        <div key={toast.key} className="create-3d__toast">
          {toast.text}
        </div>
      )}

      {baked && baked.local && (
        <BakeHud
          icon={<IconSun size={20} />}
          {...localHudProps(localStatus, localBake.paused)}
          onPause={localStatus && localStatus.phase === 'baking' ? () => setLocalBake((v) => ({ ...v, paused: !v.paused })) : null}
          paused={localBake.paused}
          onStop={
            localStatus && localStatus.phase === 'baking' && localStatus.samples > 0
              ? () => setLocalBake((v) => ({ ...v, paused: false, finishSignal: v.finishSignal + 1 }))
              : null
          }
          onClose={() => {
            setSaveOpen(false);
            setBaked(null);
          }}
          onSave={localStatus && localStatus.phase === 'done' && !bakedSaved ? () => setSaveOpen(true) : null}
          saved={bakedSaved}
          saveLabel={onFinishDesign ? 'پایان طراحی' : null}
        />
      )}

      {live && <LiveHud live={live} selfName={liveName} />}

      {viewerOnly && bakedStatus !== 'ready' && (
        <div className="create-3d__viewer-loading">
          {bakedStatus === 'error' ? (
            <p>بارگذاریِ خانه ناموفق بود — اینترنت را بررسی کنید و دوباره «ورود» را بزنید.</p>
          ) : (
            <>
              <p>در حالِ آماده کردنِ خانه…</p>
              <div className="create-3d__viewer-bar">
                <span style={{ width: `${Math.round(bakedProgress * 100)}%` }} />
              </div>
            </>
          )}
        </div>
      )}

      {!viewerOnly && baked && !baked.local && (
        <BakeHud
          icon={<IconServer size={19} />}
          title={bakedStatus === 'loading' ? 'بارگذاریِ رندر' : bakedStatus === 'error' ? 'بارگذاری ناموفق بود' : 'رندرِ واقعی'}
          meta={bakedStatus === 'loading' ? 'از سرور' : bakedStatus === 'error' ? 'دوباره «نمایش» را بزنید' : 'رندرِ سرور'}
          progress={bakedStatus === 'loading' ? bakedProgress : null}
          tone={bakedStatus === 'error' ? 'error' : bakedStatus === 'loading' ? 'working' : 'done'}
          onClose={() => {
            setSaveOpen(false);
            setBaked(null);
          }}
          onSave={bakedStatus === 'ready' && baked.id && !bakedSaved ? () => setSaveOpen(true) : null}
          saved={bakedSaved}
          saveLabel={onFinishDesign ? 'پایان طراحی' : null}
        />
      )}

      {saveOpen && baked && (
        <SaveDesignSheet
          key={bakedKey}
          prepare={prepareDesignFiles}
          mode={onFinishDesign ? 'finish' : 'upload'}
          onPrepared={(prepared) => onFinishDesign && onFinishDesign(prepared)}
          onClose={() => setSaveOpen(false)}
          onSaved={() => setSavedKeys((prev) => new Set(prev).add(bakedKey))}
        />
      )}

      {!viewerOnly && (
      <RenderUploadButton
        bakedActive={Boolean(baked)}
        bakedStatus={bakedStatus}
        onShowBaked={(r) => {
          setSelectedWallId(null);
          setSelectedSide(null);
          setSelectedDoorId(null);
          setSelectedOpeningId(null);
          setSelectedWindowId(null);
          setSelectedFloorId(null);
          setSelectedCeilingId(null);
          setEngineeringOn(false);
          setBakedStatus('loading');
          setBakedProgress(0);
          setBaked(r);
        }}
        onHideBaked={() => setBaked(null)}
        onLocalBake={() => {
          setSelectedWallId(null);
          setSelectedSide(null);
          setSelectedDoorId(null);
          setSelectedOpeningId(null);
          setSelectedWindowId(null);
          setSelectedFloorId(null);
          setSelectedCeilingId(null);
          setEngineeringOn(false);
          setLocalBake({ paused: false, finishSignal: 0 });
          setLocalStatus({ phase: 'prepare', samples: 0, target: 0, elapsedMs: 0 });
          setBaked({ local: true, key: Date.now() });
        }}
        exportRef={exportRef}
        onBeforeExport={() => {
          // نوارهای آبیِ انتخاب در فایلِ رندر نیایند
          setSelectedWallId(null);
          setSelectedSide(null);
          setSelectedDoorId(null);
          setSelectedOpeningId(null);
          setSelectedWindowId(null);
          setSelectedFloorId(null);
          setSelectedCeilingId(null);
        }}
      />
      )}

      <button type="button" className="create-3d__back" onClick={onBack}>
        {viewerOnly ? <IconClose size={18} /> : <IconPlan size={18} />}
        <span>{viewerOnly ? 'خروج' : 'دوبعدی'}</span>
      </button>
    </div>
  );
}

export default Create3D;